// Canvas-Style Image Editor - Like Canvas/Adobe Express
// Drag, drop, move, and resize images on canvas

class CanvasObject {
    constructor(image, x = 0, y = 0, type = 'image') {
        this.id = Date.now() + Math.random();
        this.type = type;
        this.image = image;
        this.x = x;
        this.y = y;
        this.width = image ? image.width : 200;
        this.height = image ? image.height : 50;
        this.scaleX = 1;
        this.scaleY = 1;
        this.rotation = 0;
        this.opacity = 1;
        
        // Shape cropping
        this.cropShape = null; // 'square', 'circle', or null
        
        // Store original image for crop mode (to show uncropped version)
        this.originalImage = null;
        this.originalX = null;
        this.originalY = null;
        this.originalWidth = null;
        this.originalHeight = null;
        
        // Store the very first imported image (for reset functionality)
        this.firstImportedImage = null;
        this.firstImportedX = null;
        this.firstImportedY = null;
        this.firstImportedWidth = null;
        this.firstImportedHeight = null;
        
        // Outline
        this.outlineWidth = 0;
        this.outlineColor = '#000000';
        
        // Track if image is PNG with transparency
        this.hasTransparency = false;
        
        // Text properties (if type is 'text')
        if (type === 'text') {
            this.text = 'New Text';
            this.fontFamily = 'Arial';
            this.fontSize = 84;
            this.textColor = '#000000';
            this.bold = false;
            this.italic = false;
            this.underline = false;
            this.textAlign = 'left'; // 'left', 'center', 'right'
            this.isEditing = false;
        }
        
        // Shape properties (if type is 'circle' or 'square')
        if (type === 'circle' || type === 'square') {
            // Square defaults to orange, circle to blue
            this.fillColor = type === 'square' ? '#ff8c00' : '#e96d00';
            this.strokeColor = '#000000';
            this.strokeWidth = 0;
        }
    }

    getBounds() {
        return {
            x: this.x,
            y: this.y,
            width: this.width * this.scaleX,
            height: this.height * this.scaleY
        };
    }

    containsPoint(x, y) {
        const bounds = this.getBounds();
        return x >= bounds.x && x <= bounds.x + bounds.width &&
               y >= bounds.y && y <= bounds.y + bounds.height;
    }

    getResizeHandle(x, y, handleSize = 30) {
        const bounds = this.getBounds();
        const handles = [
            { corner: 'nw', x: bounds.x, y: bounds.y },
            { corner: 'ne', x: bounds.x + bounds.width, y: bounds.y },
            { corner: 'sw', x: bounds.x, y: bounds.y + bounds.height },
            { corner: 'se', x: bounds.x + bounds.width, y: bounds.y + bounds.height }
        ];

        for (const handle of handles) {
            const dist = Math.sqrt(Math.pow(x - handle.x, 2) + Math.pow(y - handle.y, 2));
            if (dist <= handleSize) {
                return handle.corner;
            }
        }
        return null;
    }
}

class CanvasEditor {
    constructor() {
        this.canvas = null;
        this.ctx = null;
        this.objects = [];
        this.selectedObject = null;
        this.selectedObjects = []; // Multi-select support
        this.isDragging = false;
        this.isResizing = false;
        this.dragStart = { x: 0, y: 0 };
        this.initialPositions = []; // Store initial positions for multi-select drag
        this.resizeCorner = null;
        this.initialBounds = null;
        this.currentTool = null; // 'text', 'crop', etc.
        this.wasDragging = false; // Track if object was actually moved/resized
        this.wasResizing = false;
        
        // Manual crop state
        this.isCropping = false;
        this.cropBox = null; // { x, y, width, height }
        this.cropStart = { x: 0, y: 0 };
        this.isDraggingCrop = false;
        this.isResizingCrop = false;

        // Per-object async processing state (e.g. background removal), so only
        // the affected image shows a loading indicator instead of blocking the page
        this.processingObject = null;
        this._processingAnimFrame = null;
        
        // High resolution canvas dimensions (300 DPI for print quality)
        // Increased width while maintaining 300 DPI
        this.canvasWidth = 4000;
        this.canvasHeight = 3000;
        this.zoom = 1;
        this.aspectRatio = this.canvasWidth / this.canvasHeight; // Dynamic aspect ratio
        this.displayScale = 1; // Scale factor for display vs actual resolution
        
        // Undo/Redo system
        this.history = [];
        this.historyIndex = -1;
        this.maxHistorySize = 50;
        
        // Copy/Paste system
        this.clipboard = null;
        
        this.init();
    }

    init() {
        this.setupCanvas();
        this.setupEventListeners();
        this.updateCanvasSize();
        this.draw();
        // Save initial state
        this.saveState();
    }

    setupCanvas() {
        this.canvas = document.getElementById('imageCanvas');
        if (!this.canvas) return;
        
        this.ctx = this.canvas.getContext('2d');
        
        // Draw initial instruction
        this.drawInstructions();
    }

    updateCanvasSize() {
        if (!this.canvas) return;
        
        const container = this.canvas.parentElement;
        if (!container) return;
        
        // Get available space (minimal padding for full screen)
        const containerRect = container.getBoundingClientRect();
        const padding = 20;
        const availableWidth = containerRect.width - padding;
        const availableHeight = containerRect.height - padding;
        
        // Calculate display dimensions maintaining aspect ratio
        let displayWidth = availableWidth;
        let displayHeight = availableHeight;
        
        // Use the smaller dimension to maintain aspect ratio
        if (availableWidth / availableHeight > this.aspectRatio) {
            displayWidth = availableHeight * this.aspectRatio;
        } else {
            displayHeight = availableWidth / this.aspectRatio;
        }
        
        // Canvas maintains high resolution internally, but displays scaled
        // Keep internal canvas at high resolution (2480x3508)
        // Scale display to fit screen
        this.displayScale = Math.min(displayWidth / this.canvasWidth, displayHeight / this.canvasHeight);
        
        // Set canvas internal resolution (high res)
        this.canvas.width = this.canvasWidth;
        this.canvas.height = this.canvasHeight;
        
        // Set CSS display size (scaled to fit screen)
        this.canvas.style.width = (this.canvasWidth * this.displayScale) + 'px';
        this.canvas.style.height = (this.canvasHeight * this.displayScale) + 'px';
        this.canvas.style.maxWidth = '100%';
        this.canvas.style.maxHeight = '100%';
        
        // Redraw
        this.drawInstructions();
        this.draw();
    }

    drawInstructions() {
        if (!this.ctx || !this.canvas || this.objects.length > 0) return;
        
        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Create instruction text objects - title (bold) and body (regular)
        const titleText = 'How to use the Image Editor';
        const bodyText =
        'Drag & drop images here or click Upload Image\n\n'+
        'Click to select | Drag to move | Drag corners to resize\n\n'+
        'To add text click Add Text, then click canvas to add text\n\n'+
        'Press Delete key to remove selected object\n\n'+
        'Click Download to save your image';
        
        const baseSize = Math.min(this.canvas.width, this.canvas.height);
        const fontSize = 78; // Default instruction text size
        
        // Create title text object (bold)
        const titleObj = new CanvasObject(null, 0, 0, 'text');
        titleObj.text = titleText;
        titleObj.fontFamily = 'Questrial';
        titleObj.fontSize = fontSize;
        titleObj.textColor = '#666666'; // Dark grey color
        titleObj.bold = true; // Make title bold
        this.objects.push(titleObj);
        
        // Create body text object (regular)
        const bodyObj = new CanvasObject(null, 0, 0, 'text');
        bodyObj.text = bodyText;
        bodyObj.fontFamily = 'Questrial';
        bodyObj.fontSize = fontSize;
        bodyObj.textColor = '#666666'; // Dark grey color
        this.objects.push(bodyObj);
        
        // Center the instruction text (same formula as images)
        // First, measure both text objects
        this.ctx.font = `bold ${fontSize}px Questrial`;
        const titleMetrics = this.ctx.measureText(titleText);
        const titleWidth = titleMetrics.width;
        const titleHeight = fontSize * 1.5;
        
        this.ctx.font = `${fontSize}px Questrial`;
        const bodyLines = bodyText.split('\n');
        const lineHeight = fontSize * 1.5;
        let maxBodyWidth = 0;
        bodyLines.forEach(line => {
            if (line.trim()) {
                const metrics = this.ctx.measureText(line);
                maxBodyWidth = Math.max(maxBodyWidth, metrics.width);
            }
        });
        
        const maxWidth = Math.max(titleWidth, maxBodyWidth);
        const totalHeight = titleHeight + (bodyLines.length * lineHeight);
        
        // Set dimensions
        titleObj.width = titleWidth;
        titleObj.height = titleHeight;
        bodyObj.width = maxBodyWidth;
        bodyObj.height = bodyLines.length * lineHeight;
        
        // Center horizontally and vertically on the canvas
        const centerX = (this.canvasWidth / 2) - (maxWidth / 2);
        const centerY = (this.canvasHeight / 2) - (totalHeight / 2);
        
        titleObj.x = centerX;
        titleObj.y = centerY;
        bodyObj.x = centerX;
        bodyObj.y = centerY + titleHeight + (lineHeight * 2); // Add spacing after title
        
        // Mark both as instruction text
        titleObj.isInstructionText = true;
        bodyObj.isInstructionText = true;
        
        this.draw();
    }

    setupEventListeners() {
        // File upload
        const fileInput = document.getElementById('fileInput');
        if (fileInput) {
            fileInput.addEventListener('change', (e) => this.handleFileSelect(e));
        }

        // Canvas drag and drop
        const canvasContainer = document.getElementById('canvasContainer');
        if (canvasContainer) {
            canvasContainer.addEventListener('dragover', (e) => this.handleDragOver(e));
            canvasContainer.addEventListener('dragleave', (e) => this.handleDragLeave(e));
            canvasContainer.addEventListener('drop', (e) => this.handleDrop(e));
        }

        // Canvas pointer events (covers mouse, touch, and pen in one set of listeners)
        if (this.canvas) {
            this.canvas.style.touchAction = 'none';
            this.canvas.addEventListener('pointerdown', (e) => this.handleMouseDown(e));
            this.canvas.addEventListener('pointermove', (e) => this.handleMouseMove(e));
            this.canvas.addEventListener('pointerup', (e) => this.handleMouseUp(e));
            this.canvas.addEventListener('pointercancel', (e) => this.handleMouseUp(e));
            this.canvas.addEventListener('pointerleave', (e) => this.handleMouseUp(e));
            this.canvas.addEventListener('dblclick', (e) => this.handleDoubleClick(e));
        }

        // Download button
        const downloadBtn = document.getElementById('downloadBtn');
        if (downloadBtn) {
            downloadBtn.addEventListener('click', () => this.download());
        }

        // Tool buttons
        const textTool = document.getElementById('textTool');
        const cropTool = document.getElementById('cropTool');
        const cropSquareTool = document.getElementById('cropSquareTool');
        const cropCircleTool = document.getElementById('cropCircleTool');
        const circleTool = document.getElementById('circleTool');
        const squareTool = document.getElementById('squareTool');
        
        if (textTool) {
            textTool.addEventListener('click', () => this.selectTool('text'));
        }
        if (cropTool) {
            cropTool.addEventListener('click', () => this.selectTool('crop'));
        }
        if (cropSquareTool) {
            cropSquareTool.addEventListener('click', () => this.applyCropToShape('square'));
        }
        if (cropCircleTool) {
            cropCircleTool.addEventListener('click', () => this.applyCropToShape('circle'));
        }
        if (circleTool) {
            circleTool.addEventListener('click', () => this.selectTool('circle'));
        }
        if (squareTool) {
            squareTool.addEventListener('click', () => this.selectTool('square'));
        }

        // Image properties
        const outlineWidth = document.getElementById('outlineWidth');
        const outlineColor = document.getElementById('outlineColor');
        if (outlineWidth) {
            outlineWidth.addEventListener('input', (e) => {
                const valueDisplay = document.getElementById('outlineWidthValue');
                if (valueDisplay) valueDisplay.textContent = e.target.value + 'px';
                this.updateOutline();
            });
        }
        if (outlineColor) {
            outlineColor.addEventListener('input', () => this.updateOutline());
        }
        
        // Reset image button
        const resetImageBtn = document.getElementById('resetImageBtn');
        if (resetImageBtn) {
            resetImageBtn.addEventListener('click', () => this.resetImageToOriginal());
        }

        // Remove background button
        const removeBackgroundBtn = document.getElementById('removeBackgroundBtn');
        if (removeBackgroundBtn) {
            removeBackgroundBtn.addEventListener('click', () => this.removeBackground());
        }

        // Text properties
        const applyTextBtn = document.getElementById('applyTextBtn');
        const textContent = document.getElementById('textContent');
        const textFontFamily = document.getElementById('textFontFamily');
        const textSize = document.getElementById('textSize');
        const textColor = document.getElementById('textColor');
        const textBoldBtn = document.getElementById('textBoldBtn');
        const textItalicBtn = document.getElementById('textItalicBtn');
        const textUnderlineBtn = document.getElementById('textUnderlineBtn');
        const textAlignBtn = document.getElementById('textAlignBtn');

        if (applyTextBtn) {
            applyTextBtn.addEventListener('click', () => this.applyTextProperties());
        }
        if (textContent) {
            textContent.addEventListener('input', () => this.updateTextPreview());
        }
        if (textFontFamily) {
            textFontFamily.addEventListener('change', () => {
                // Update select element's font to show preview
                const selectedValue = textFontFamily.value;
                textFontFamily.style.fontFamily = selectedValue;
                this.updateTextPreview();
            });
            // Set initial font preview
            textFontFamily.style.fontFamily = textFontFamily.value;
            // Update font preview on mouseover for options
            textFontFamily.addEventListener('mouseover', (e) => {
                if (e.target.tagName === 'OPTION' && e.target.value) {
                    textFontFamily.style.fontFamily = e.target.value;
                }
            });
        }
        if (textSize) {
            textSize.addEventListener('input', (e) => {
                const valueDisplay = document.getElementById('textSizeValue');
                if (valueDisplay) valueDisplay.textContent = e.target.value;
                this.updateTextPreview();
            });
        }
        if (textColor) {
            textColor.addEventListener('input', () => this.updateTextPreview());
        }
        if (textBoldBtn) {
            textBoldBtn.addEventListener('click', () => this.toggleTextFormat('bold'));
        }
        if (textItalicBtn) {
            textItalicBtn.addEventListener('click', () => this.toggleTextFormat('italic'));
        }
        if (textUnderlineBtn) {
            textUnderlineBtn.addEventListener('click', () => this.toggleTextFormat('underline'));
        }
        if (textAlignBtn) {
            textAlignBtn.addEventListener('click', () => this.cycleTextAlign());
        }

        // Shape properties
        const applyShapeBtn = document.getElementById('applyShapeBtn');
        const shapeFillColor = document.getElementById('shapeFillColor');
        const shapeStrokeColor = document.getElementById('shapeStrokeColor');
        const shapeStrokeWidth = document.getElementById('shapeStrokeWidth');
        
        if (applyShapeBtn) {
            applyShapeBtn.addEventListener('click', () => this.applyShapeProperties());
        }
        if (shapeFillColor) {
            shapeFillColor.addEventListener('input', () => this.updateShapePreview());
        }
        if (shapeStrokeColor) {
            shapeStrokeColor.addEventListener('input', () => this.updateShapePreview());
        }
        if (shapeStrokeWidth) {
            shapeStrokeWidth.addEventListener('input', (e) => {
                const valueDisplay = document.getElementById('shapeStrokeWidthValue');
                if (valueDisplay) valueDisplay.textContent = e.target.value + 'px';
                this.updateShapePreview();
            });
        }

        // Window resize - update canvas size responsively
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                this.updateCanvasSize();
            }, 100);
        });
        
        // Keyboard events
        document.addEventListener('keydown', (e) => {
            // Don't interfere with input fields or textarea (inline text editing)
            if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
                // Allow all normal text editing keys (Delete, Backspace, etc.) in textarea
                if (e.key === 'Delete' || e.key === 'Backspace') {
                    return; // Let browser handle Delete/Backspace as backspace in textarea
                }
                // Allow Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A in input fields
                if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'a')) {
                    return; // Let browser handle it
                }
                // Allow Ctrl+Z in input fields for undo within the field
                if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                    return; // Let browser handle it
                }
            }
            
            // Check if any selected text object is in editing mode
            if (this.selectedObject && this.selectedObject.type === 'text' && this.selectedObject.isEditing) {
                // If text is being edited, let Delete/Backspace work as normal text editing
                if (e.key === 'Delete' || e.key === 'Backspace') {
                    return; // Don't delete the object, let textarea handle it
                }
            }
            
            // Ctrl+Z or Cmd+Z for undo
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                this.undo();
                return;
            }
            
            // Ctrl+Shift+Z or Cmd+Shift+Z for redo
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
                e.preventDefault();
                this.redo();
                return;
            }
            
            // Ctrl+C or Cmd+C for copy
            if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                e.preventDefault();
                this.copySelectedObject();
                return;
            }
            
            // Ctrl+V or Cmd+V for paste
            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                e.preventDefault();
                this.pasteObject();
                return;
            }
            
            // Delete or Backspace
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                this.deleteSelectedObject();
            }
        });
        
        // Mobile tool toggle
        const showToolsBtn = document.getElementById('showToolsBtn');
        const showPropertiesBtn = document.getElementById('showPropertiesBtn');
        const toolsSidebar = document.getElementById('toolsSidebar');
        const propertiesSidebar = document.getElementById('propertiesSidebar');
        
        if (showToolsBtn && showPropertiesBtn) {
            showToolsBtn.addEventListener('click', () => {
                showToolsBtn.classList.add('active');
                showPropertiesBtn.classList.remove('active');
                if (toolsSidebar) toolsSidebar.classList.add('active');
                if (propertiesSidebar) propertiesSidebar.classList.remove('active');
            });
            
            showPropertiesBtn.addEventListener('click', () => {
                showPropertiesBtn.classList.add('active');
                showToolsBtn.classList.remove('active');
                if (propertiesSidebar) propertiesSidebar.classList.add('active');
                if (toolsSidebar) toolsSidebar.classList.remove('active');
            });
        }
        
        // Upload button
        const uploadBtn = document.getElementById('uploadBtn');
        if (uploadBtn && fileInput) {
            uploadBtn.addEventListener('click', () => {
                fileInput.click();
            });
        }
        
        // Initial size update
        setTimeout(() => this.updateCanvasSize(), 100);
    }

    getCanvasCoordinates(e) {
        const rect = this.canvas.getBoundingClientRect();
        // Convert display coordinates to canvas coordinates
        const displayX = e.clientX - rect.left;
        const displayY = e.clientY - rect.top;
        
        // Scale back to internal canvas coordinates
        return {
            x: displayX / this.displayScale,
            y: displayY / this.displayScale
        };
    }

    handleFileSelect(event) {
        const files = Array.from(event.target.files).filter(file => file.type.startsWith('image/'));
        files.forEach(file => this.loadImage(file));
    }

    handleDragOver(event) {
        event.preventDefault();
        event.stopPropagation();
        const container = document.getElementById('canvasContainer');
        if (container) container.classList.add('drag-over');
    }

    handleDragLeave(event) {
        event.preventDefault();
        const container = document.getElementById('canvasContainer');
        if (container) container.classList.remove('drag-over');
    }

    handleDrop(event) {
        event.preventDefault();
        event.stopPropagation();
        const container = document.getElementById('canvasContainer');
        if (container) container.classList.remove('drag-over');
        
        const files = Array.from(event.dataTransfer.files).filter(file => file.type.startsWith('image/'));
        
        // Center images in canvas area when dropped
        files.forEach((file, index) => {
            setTimeout(() => {
                this.loadImage(file);
            }, index * 50);
        });
    }

    loadImage(file, x = null, y = null) {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const centerX = x !== null ? x : (this.canvasWidth / 2) - (img.width / 2);
                const centerY = y !== null ? y : (this.canvasHeight / 2) - (img.height / 2);
                
                this.saveState();
                const obj = new CanvasObject(img, centerX, centerY, 'image');
                
                // Store the very first imported image for reset functionality
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                const firstImg = new Image();
                firstImg.src = canvas.toDataURL('image/png');
                obj.firstImportedImage = firstImg;
                obj.firstImportedX = centerX;
                obj.firstImportedY = centerY;
                obj.firstImportedWidth = img.width;
                obj.firstImportedHeight = img.height;
                
                // Check if image is PNG with transparency
                if (file.type === 'image/png') {
                    this.checkImageTransparency(img, obj);
                }
                
                this.objects.push(obj);
                this.selectedObjects = [obj];
                this.selectedObject = obj;
                this.updatePropertiesPanel();
                this.updateLayersList();
                this.draw();
                this.updateDownloadButton();
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    }
    
    checkImageTransparency(img, obj) {
        // Create a temporary canvas to check for transparency
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.drawImage(img, 0, 0);
        
        // Sample pixels to check for transparency
        // Check a grid of pixels for efficiency (not every single pixel)
        const sampleSize = Math.max(1, Math.floor(Math.min(img.width, img.height) / 50));
        let hasTransparentPixels = false;
        
        for (let y = 0; y < img.height; y += sampleSize) {
            for (let x = 0; x < img.width; x += sampleSize) {
                const pixelData = tempCtx.getImageData(x, y, 1, 1).data;
                const alpha = pixelData[3];
                if (alpha < 255) {
                    hasTransparentPixels = true;
                    break;
                }
            }
            if (hasTransparentPixels) break;
        }
        
        if (hasTransparentPixels) {
            obj.hasTransparency = true;
            // Note: Outline width is not automatically applied - user can set it manually if desired
        }
    }

    addTextObject(x, y) {
        this.saveState();
        const obj = new CanvasObject(null, x, y, 'text');
        this.objects.push(obj);
        this.selectedObjects = [obj];
        this.selectedObject = obj;
        this.updatePropertiesPanel();
        this.updateLayersList();
        this.draw();
        this.updateDownloadButton();
    }

    addShapeObject(x, y, shapeType) {
        this.saveState();
        const defaultSize = 150;
        const obj = new CanvasObject(null, x - (defaultSize / 2), y - (defaultSize / 2), shapeType);
        obj.width = defaultSize;
        obj.height = defaultSize;
        this.objects.push(obj);
        this.selectedObjects = [obj];
        this.selectedObject = obj;
        this.updatePropertiesPanel();
        this.updateLayersList();
        this.draw();
        this.updateDownloadButton();
    }

    selectTool(tool) {
        this.currentTool = tool;
        // Update UI
        document.querySelectorAll('.tool-btn').forEach(btn => btn.classList.remove('active'));
        const toolBtn = document.querySelector(`[data-tool="${tool}"]`);
        if (toolBtn) toolBtn.classList.add('active');
        
        // For text tool, immediately add text to center of canvas
        if (tool === 'text') {
            const centerX = this.canvasWidth / 2;
            const centerY = this.canvasHeight / 2;
            this.addTextObject(centerX, centerY);
            this.currentTool = null;
            if (toolBtn) toolBtn.classList.remove('active');
            return;
        }
        
        // For shape tools, immediately add shape to center of canvas
        if (tool === 'circle' || tool === 'square') {
            const centerX = this.canvasWidth / 2;
            const centerY = this.canvasHeight / 2;
            this.addShapeObject(centerX, centerY, tool);
            this.currentTool = null;
            if (toolBtn) toolBtn.classList.remove('active');
            return;
        }
        
        // Initialize crop mode
        if (tool === 'crop') {
            if (!this.selectedObject || this.selectedObject.type !== 'image') {
                this.showToast('Please select an image first', 'error');
                this.currentTool = null;
                if (toolBtn) toolBtn.classList.remove('active');
                return;
            }
            this.startCropMode();
        } else {
            this.exitCropMode();
        }
    }

    startCropMode() {
        this.isCropping = true;
        const obj = this.selectedObject;
        
        // Store original image and dimensions if not already stored (for showing uncropped version in crop mode)
        if (!obj.originalImage && obj.image) {
            // Store dimensions immediately
            obj.originalX = obj.x;
            obj.originalY = obj.y;
            obj.originalWidth = obj.width;
            obj.originalHeight = obj.height;
            
            // Create a copy of the original image
            const canvas = document.createElement('canvas');
            canvas.width = obj.image.width;
            canvas.height = obj.image.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(obj.image, 0, 0);
            const originalImg = new Image();
            
            // Set image source - use the image directly from canvas for immediate use
            const dataURL = canvas.toDataURL('image/png');
            originalImg.src = dataURL;
            
            // Store the image immediately (data URL should be ready)
            obj.originalImage = originalImg;
            
            // Also ensure image is loaded before using it
            if (!originalImg.complete) {
                originalImg.onload = () => {
                    // Redraw once original image is fully loaded
                    this.draw();
                };
            }
        }
        
        // If crop box already exists (user clicked crop tool again), keep it
        // Otherwise, initialize crop box to cover the current cropped image
        if (!this.cropBox) {
            const bounds = obj.getBounds();
            this.cropBox = {
                x: bounds.x,
                y: bounds.y,
                width: bounds.width,
                height: bounds.height
            };
        }
        
        // Ensure crop box is valid
        if (!this.cropBox || this.cropBox.width <= 0 || this.cropBox.height <= 0) {
            const fallbackBounds = obj.getBounds();
            this.cropBox = {
                x: fallbackBounds.x,
                y: fallbackBounds.y,
                width: fallbackBounds.width,
                height: fallbackBounds.height
            };
        }
        
        this.draw();
    }
    
    updateOriginalImageReference(obj) {
        if (!obj || obj.type !== 'image' || !obj.image) return;
        
        // Create a canvas with the current resized dimensions
        const bounds = obj.getBounds();
        const canvas = document.createElement('canvas');
        canvas.width = bounds.width;
        canvas.height = bounds.height;
        const ctx = canvas.getContext('2d');
        
        // Draw the current image at its current size
        ctx.drawImage(obj.image, 0, 0, bounds.width, bounds.height);
        
        // Create new image from the canvas
        const newOriginalImg = new Image();
        newOriginalImg.onload = () => {
            // Update original image and dimensions to match current resized state
            obj.originalImage = newOriginalImg;
            obj.originalX = obj.x;
            obj.originalY = obj.y;
            obj.originalWidth = bounds.width;
            obj.originalHeight = bounds.height;
            
            // Reset scale since the image is now at its "original" size
            obj.scaleX = 1;
            obj.scaleY = 1;
            obj.width = bounds.width;
            obj.height = bounds.height;
            
            this.draw();
        };
        newOriginalImg.src = canvas.toDataURL('image/png');
    }

    exitCropMode() {
        this.isCropping = false;
        this.cropBox = null;
        this.draw();
    }

    applyCropToShape(shape) {
        if (!this.selectedObject || this.selectedObject.type !== 'image') {
            this.showToast('Please select an image first', 'error');
            return;
        }
        
        this.saveState();
        
        // Exit manual crop mode if active
        this.exitCropMode();
        this.currentTool = null;
        document.querySelectorAll('.tool-btn').forEach(btn => btn.classList.remove('active'));
        
        this.selectedObject.cropShape = shape;
        this.draw();
        this.showToast(`Image cropped to ${shape}`, 'success');
    }

    applyManualCrop(callback) {
        if (!this.selectedObject || !this.cropBox || this.selectedObject.type !== 'image') {
            if (callback) callback();
            return;
        }
        
        // Validate crop box
        if (this.cropBox.width <= 0 || this.cropBox.height <= 0) {
            this.showToast('Invalid crop area. Please drag to create a crop box.', 'error');
            if (callback) callback();
            return;
        }
        
        this.saveState();
        
        const obj = this.selectedObject;
        
        // Use original image and bounds for cropping if available
        const sourceImage = obj.originalImage || obj.image;
        let sourceBounds;
        if (obj.originalImage && obj.originalX !== null) {
            // Use original bounds
            sourceBounds = {
                x: obj.originalX,
                y: obj.originalY,
                width: obj.originalWidth,
                height: obj.originalHeight
            };
        } else {
            // Use current bounds
            sourceBounds = obj.getBounds();
        }
        
        // Ensure crop box is within bounds (relative to source bounds)
        const cropX = Math.max(0, Math.min(this.cropBox.x - sourceBounds.x, sourceBounds.width));
        const cropY = Math.max(0, Math.min(this.cropBox.y - sourceBounds.y, sourceBounds.height));
        const cropWidth = Math.max(1, Math.min(this.cropBox.width, sourceBounds.width - cropX));
        const cropHeight = Math.max(1, Math.min(this.cropBox.height, sourceBounds.height - cropY));
        
        // Calculate crop coordinates relative to the source image
        const scaleX = sourceImage.width / sourceBounds.width;
        const scaleY = sourceImage.height / sourceBounds.height;
        
        const sourceX = cropX * scaleX;
        const sourceY = cropY * scaleY;
        const sourceWidth = cropWidth * scaleX;
        const sourceHeight = cropHeight * scaleY;
        
        // Create a temporary canvas to crop the image
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        tempCanvas.width = sourceWidth;
        tempCanvas.height = sourceHeight;
        
        // Draw the cropped portion from the source image
        tempCtx.drawImage(
            sourceImage,
            sourceX, sourceY, sourceWidth, sourceHeight,
            0, 0, sourceWidth, sourceHeight
        );
        
        // Create new image from cropped canvas
        const newImg = new Image();
        newImg.onload = () => {
            // Keep the original image reference for future crop operations
            if (!obj.originalImage) {
                // Store the original image before replacing
                const canvas = document.createElement('canvas');
                canvas.width = obj.image.width;
                canvas.height = obj.image.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(obj.image, 0, 0);
                const originalImg = new Image();
                originalImg.src = canvas.toDataURL('image/png');
                obj.originalImage = originalImg;
            }
            
            obj.image = newImg;
            obj.width = newImg.width;
            obj.height = newImg.height;
            // Adjust position to keep crop box position
            obj.x = sourceBounds.x + cropX;
            obj.y = sourceBounds.y + cropY;
            // Keep the crop box size (don't reset to full image) for further adjustments
            // The crop box position and size remain the same
            this.cropBox = {
                x: this.cropBox.x,
                y: this.cropBox.y,
                width: this.cropBox.width,
                height: this.cropBox.height
            };
            this.draw();
            this.showToast('Image cropped successfully', 'success');
            if (callback) callback();
        };
        newImg.onerror = () => {
            this.showToast('Failed to crop image', 'error');
            if (callback) callback();
        };
        newImg.src = tempCanvas.toDataURL('image/png');
    }

    getCropResizeHandle(x, y) {
        if (!this.cropBox) return null;
        
        // Larger detection size for easier hover detection and clicking
        const handleSize = 40 / this.zoom; // Increased for better visibility and easier clicking
        const tolerance = 30 / this.zoom; // Increased for easier clicking on side handles
        
        // Check corner handles first (they have priority)
        const cornerHandles = [
            { name: 'nw', x: this.cropBox.x, y: this.cropBox.y },
            { name: 'ne', x: this.cropBox.x + this.cropBox.width, y: this.cropBox.y },
            { name: 'sw', x: this.cropBox.x, y: this.cropBox.y + this.cropBox.height },
            { name: 'se', x: this.cropBox.x + this.cropBox.width, y: this.cropBox.y + this.cropBox.height }
        ];
        
        for (const handle of cornerHandles) {
            if (Math.abs(x - handle.x) <= handleSize && Math.abs(y - handle.y) <= handleSize) {
                return handle.name;
            }
        }
        
        // Check side handles (exclude corner areas to avoid conflicts)
        const cornerExclusion = handleSize * 1.5; // Exclude area near corners
        
        // Top edge (excluding corners)
        if (x >= this.cropBox.x + cornerExclusion && x <= this.cropBox.x + this.cropBox.width - cornerExclusion &&
            y >= this.cropBox.y - tolerance && y <= this.cropBox.y + tolerance) {
            return 'n';
        }
        // Bottom edge (excluding corners)
        if (x >= this.cropBox.x + cornerExclusion && x <= this.cropBox.x + this.cropBox.width - cornerExclusion &&
            y >= this.cropBox.y + this.cropBox.height - tolerance && y <= this.cropBox.y + this.cropBox.height + tolerance) {
            return 's';
        }
        // Left edge (excluding corners)
        if (x >= this.cropBox.x - tolerance && x <= this.cropBox.x + tolerance &&
            y >= this.cropBox.y + cornerExclusion && y <= this.cropBox.y + this.cropBox.height - cornerExclusion) {
            return 'w';
        }
        // Right edge (excluding corners)
        if (x >= this.cropBox.x + this.cropBox.width - tolerance && x <= this.cropBox.x + this.cropBox.width + tolerance &&
            y >= this.cropBox.y + cornerExclusion && y <= this.cropBox.y + this.cropBox.height - cornerExclusion) {
            return 'e';
        }
        
        return null;
    }


    updateOutline() {
        if (!this.selectedObject || this.selectedObject.type !== 'image') return;
        
        const outlineWidth = document.getElementById('outlineWidth');
        const outlineColor = document.getElementById('outlineColor');
        
        if (outlineWidth) {
            this.selectedObject.outlineWidth = parseInt(outlineWidth.value);
        }
        if (outlineColor) {
            this.selectedObject.outlineColor = outlineColor.value;
        }
        
        this.draw();
    }
    
    resetImageToOriginal() {
        if (!this.selectedObject || this.selectedObject.type !== 'image') {
            this.showToast('Please select an image first', 'error');
            return;
        }
        
        const obj = this.selectedObject;
        
        // Check if we have the first imported image
        if (!obj.firstImportedImage) {
            this.showToast('Original image not available', 'error');
            return;
        }
        
        // Wait for the image to load if needed
        if (!obj.firstImportedImage.complete) {
            obj.firstImportedImage.onload = () => {
                this.performImageReset(obj);
            };
            return;
        }
        
        this.performImageReset(obj);
    }
    
    performImageReset(obj) {
        this.saveState();
        
        // Reset to first imported image
        obj.image = obj.firstImportedImage;
        obj.x = obj.firstImportedX;
        obj.y = obj.firstImportedY;
        obj.width = obj.firstImportedWidth;
        obj.height = obj.firstImportedHeight;
        obj.scaleX = 1;
        obj.scaleY = 1;
        obj.rotation = 0;
        obj.cropShape = null;
        
        // Reset original image references to first imported
        obj.originalImage = obj.firstImportedImage;
        obj.originalX = obj.firstImportedX;
        obj.originalY = obj.firstImportedY;
        obj.originalWidth = obj.firstImportedWidth;
        obj.originalHeight = obj.firstImportedHeight;
        
        // Exit crop mode if active
        if (this.isCropping) {
            this.exitCropMode();
        }
        
        this.updatePropertiesPanel();
        this.draw();
        this.showToast('Image reset to original', 'success');
    }

    loadBackgroundRemovalLibrary() {
        if (typeof window.imglyRemoveBackground !== 'undefined') {
            return Promise.resolve();
        }
        if (this._bgRemovalLoadPromise) {
            return this._bgRemovalLoadPromise;
        }
        // The package only ships CJS/ESM builds (no browser-global bundle) and its ESM
        // build has a bare "onnxruntime-web" import the browser can't resolve on its
        // own, so it's loaded via jsDelivr's +esm endpoint, which bundles that
        // dependency in automatically.
        this._bgRemovalLoadPromise = import('https://cdn.jsdelivr.net/npm/@imgly/background-removal@1.7.0/+esm')
            .then((mod) => {
                window.imglyRemoveBackground = mod.removeBackground;
            });
        return this._bgRemovalLoadPromise;
    }

    removeBackground() {
        if (!this.selectedObject || this.selectedObject.type !== 'image') {
            this.showToast('Please select an image first', 'error');
            return;
        }

        const obj = this.selectedObject;
        const removeBackgroundBtn = document.getElementById('removeBackgroundBtn');
        if (removeBackgroundBtn) removeBackgroundBtn.disabled = true;
        this.startProcessingIndicator(obj);

        this.loadBackgroundRemovalLibrary()
            .then(() => window.imglyRemoveBackground(obj.image.src, {
                model: 'isnet_fp16',
                output: { format: 'image/png' },
            }))
            .then((blob) => new Promise((resolve, reject) => {
                const newImg = new Image();
                newImg.onload = () => resolve(newImg);
                newImg.onerror = () => reject(new Error('Failed to load processed image'));
                newImg.src = URL.createObjectURL(blob);
            }))
            .then((newImg) => {
                this.saveState();
                obj.image = newImg;
                obj.hasTransparency = true;
                this.stopProcessingIndicator();
                if (removeBackgroundBtn) removeBackgroundBtn.disabled = false;
                this.showToast('Background removed', 'success');
            })
            .catch((err) => {
                console.error('Background removal failed:', err);
                this.stopProcessingIndicator();
                if (removeBackgroundBtn) removeBackgroundBtn.disabled = false;
                this.showToast('Background removal failed. Please try again.', 'error');
            });
    }

    toggleTextFormat(format) {
        if (!this.selectedObject || this.selectedObject.type !== 'text') return;
        
        this.saveState();
        if (format === 'bold') {
            this.selectedObject.bold = !this.selectedObject.bold;
        } else if (format === 'italic') {
            this.selectedObject.italic = !this.selectedObject.italic;
        } else if (format === 'underline') {
            this.selectedObject.underline = !this.selectedObject.underline;
        }
        
        this.updateFormatButtons();
        this.draw();
    }
    
    setTextAlign(align) {
        if (!this.selectedObject || this.selectedObject.type !== 'text') return;

        this.saveState();
        this.selectedObject.textAlign = align;
        this.updateFormatButtons();
        this.draw();
    }

    cycleTextAlign() {
        if (!this.selectedObject || this.selectedObject.type !== 'text') return;

        const order = ['left', 'center', 'right'];
        const currentIndex = order.indexOf(this.selectedObject.textAlign || 'left');
        const nextAlign = order[(currentIndex + 1) % order.length];
        this.setTextAlign(nextAlign);
    }

    updateFormatButtons() {
        if (!this.selectedObject || this.selectedObject.type !== 'text') return;

        const textBoldBtn = document.getElementById('textBoldBtn');
        const textItalicBtn = document.getElementById('textItalicBtn');
        const textUnderlineBtn = document.getElementById('textUnderlineBtn');
        const textAlignBtn = document.getElementById('textAlignBtn');

        const setPressed = (btn, isPressed) => {
            if (!btn) return;
            btn.classList.toggle('active', isPressed);
            btn.setAttribute('aria-pressed', String(isPressed));
        };

        setPressed(textBoldBtn, this.selectedObject.bold);
        setPressed(textItalicBtn, this.selectedObject.italic);
        setPressed(textUnderlineBtn, this.selectedObject.underline);

        if (textAlignBtn) {
            const align = this.selectedObject.textAlign || 'left';
            const icon = textAlignBtn.querySelector('.align-icon');
            if (icon) icon.dataset.align = align;
            const label = align.charAt(0).toUpperCase() + align.slice(1);
            textAlignBtn.title = `Text Alignment: ${label} (click to cycle)`;
            textAlignBtn.setAttribute('aria-label', `Text alignment: ${align}. Click to cycle to the next alignment.`);
        }
    }

    applyTextProperties() {
        if (!this.selectedObject || this.selectedObject.type !== 'text') return;
        
        const textContent = document.getElementById('textContent');
        const textFontFamily = document.getElementById('textFontFamily');
        const textSize = document.getElementById('textSize');
        const textColor = document.getElementById('textColor');
        
        if (textContent) this.selectedObject.text = textContent.value;
        if (textFontFamily) this.selectedObject.fontFamily = textFontFamily.value;
        if (textSize) this.selectedObject.fontSize = parseInt(textSize.value);
        if (textColor) this.selectedObject.textColor = textColor.value;
        
        // Update text dimensions (handle multi-line)
        let fontStyle = '';
        if (this.selectedObject.bold) fontStyle += 'bold ';
        if (this.selectedObject.italic) fontStyle += 'italic ';
        this.ctx.font = `${fontStyle}${this.selectedObject.fontSize}px ${this.selectedObject.fontFamily}`;
        const lines = this.selectedObject.text.split('\n');
        const lineHeight = this.selectedObject.fontSize * 1.5;
        let maxWidth = 0;
        lines.forEach(line => {
            const metrics = this.ctx.measureText(line);
            maxWidth = Math.max(maxWidth, metrics.width);
        });
        this.selectedObject.width = maxWidth;
        this.selectedObject.height = lines.length * lineHeight;
        
        this.draw();
    }

    updateTextPreview() {
        if (!this.selectedObject || this.selectedObject.type !== 'text') return;
        this.applyTextProperties();
    }

    applyShapeProperties() {
        if (!this.selectedObject || (this.selectedObject.type !== 'circle' && this.selectedObject.type !== 'square')) return;
        
        const shapeFillColor = document.getElementById('shapeFillColor');
        const shapeStrokeColor = document.getElementById('shapeStrokeColor');
        const shapeStrokeWidth = document.getElementById('shapeStrokeWidth');
        
        if (shapeFillColor) this.selectedObject.fillColor = shapeFillColor.value;
        if (shapeStrokeColor) this.selectedObject.strokeColor = shapeStrokeColor.value;
        if (shapeStrokeWidth) this.selectedObject.strokeWidth = parseInt(shapeStrokeWidth.value);
        
        this.draw();
    }

    updateShapePreview() {
        if (!this.selectedObject || (this.selectedObject.type !== 'circle' && this.selectedObject.type !== 'square')) return;
        this.applyShapeProperties();
    }

    updatePropertiesPanel() {
        // Hide all property sections
        document.querySelectorAll('.property-section[id$="Properties"]').forEach(section => {
            section.style.display = 'none';
        });
        
        if (!this.selectedObject) return;
        
        if (this.selectedObject.type === 'image') {
            const imageProps = document.getElementById('imageProperties');
            if (imageProps) {
                imageProps.style.display = 'block';
                const outlineWidth = document.getElementById('outlineWidth');
                const outlineColor = document.getElementById('outlineColor');
                if (outlineWidth) outlineWidth.value = this.selectedObject.outlineWidth;
                if (outlineColor) outlineColor.value = this.selectedObject.outlineColor;
                const valueDisplay = document.getElementById('outlineWidthValue');
                if (valueDisplay) valueDisplay.textContent = this.selectedObject.outlineWidth + 'px';
            }
        } else if (this.selectedObject.type === 'text') {
            const textProps = document.getElementById('textProperties');
            if (textProps) {
                textProps.style.display = 'block';
                const textContent = document.getElementById('textContent');
                const textFontFamily = document.getElementById('textFontFamily');
                const textSize = document.getElementById('textSize');
                const textColor = document.getElementById('textColor');
                
                if (textContent) textContent.value = this.selectedObject.text;
                if (textFontFamily) {
                    textFontFamily.value = this.selectedObject.fontFamily;
                    // Update font preview
                    textFontFamily.style.fontFamily = this.selectedObject.fontFamily;
                }
                if (textSize) {
                    textSize.value = this.selectedObject.fontSize;
                    const valueDisplay = document.getElementById('textSizeValue');
                    if (valueDisplay) valueDisplay.textContent = this.selectedObject.fontSize;
                }
                if (textColor) textColor.value = this.selectedObject.textColor;
                // Update format buttons
                this.updateFormatButtons();
            }
        } else if (this.selectedObject.type === 'circle' || this.selectedObject.type === 'square') {
            const shapeProps = document.getElementById('shapeProperties');
            if (shapeProps) {
                shapeProps.style.display = 'block';
                const fillColor = document.getElementById('shapeFillColor');
                const strokeColor = document.getElementById('shapeStrokeColor');
                const strokeWidth = document.getElementById('shapeStrokeWidth');
                
                if (fillColor) fillColor.value = this.selectedObject.fillColor || '#e96d00';
                if (strokeColor) strokeColor.value = this.selectedObject.strokeColor || '#000000';
                if (strokeWidth) {
                    strokeWidth.value = this.selectedObject.strokeWidth || 0;
                    const valueDisplay = document.getElementById('shapeStrokeWidthValue');
                    if (valueDisplay) valueDisplay.textContent = (this.selectedObject.strokeWidth || 0) + 'px';
                }
            }
        }
    }

    deleteSelectedObject() {
        if (this.selectedObjects.length === 0) return;
        
        this.saveState();
        const selectedIds = this.selectedObjects.map(obj => obj.id);
        this.objects = this.objects.filter(obj => !selectedIds.includes(obj.id));
        this.selectedObjects = [];
        this.selectedObject = null;
        this.updatePropertiesPanel();
        this.updateLayersList();
        this.draw();
        this.updateDownloadButton();
        const count = selectedIds.length;
        this.showToast(count > 1 ? `${count} objects deleted` : 'Object deleted', 'success');
    }

    // Undo/Redo system
    saveState() {
        // Remove any states after current index (when new action is performed after undo)
        if (this.historyIndex < this.history.length - 1) {
            this.history = this.history.slice(0, this.historyIndex + 1);
        }
        
        // Create deep copy of objects
        const state = JSON.stringify(this.objects.map(obj => {
            if (obj.type === 'image') {
                return {
                    type: 'image',
                    x: obj.x,
                    y: obj.y,
                    width: obj.width,
                    height: obj.height,
                    scaleX: obj.scaleX,
                    scaleY: obj.scaleY,
                    rotation: obj.rotation,
                    opacity: obj.opacity,
                    cropShape: obj.cropShape,
                    outlineWidth: obj.outlineWidth,
                    outlineColor: obj.outlineColor,
                    imageSrc: obj.image.src, // Store image source
                    id: obj.id
                };
            } else if (obj.type === 'text') {
                return {
                    type: 'text',
                    x: obj.x,
                    y: obj.y,
                    width: obj.width,
                    height: obj.height,
                    scaleX: obj.scaleX,
                    scaleY: obj.scaleY,
                    rotation: obj.rotation,
                    opacity: obj.opacity,
                    text: obj.text,
                    fontFamily: obj.fontFamily,
                    fontSize: obj.fontSize,
                    textColor: obj.textColor,
                    bold: obj.bold,
                    italic: obj.italic,
                    id: obj.id
                };
            } else if (obj.type === 'circle' || obj.type === 'square') {
                return {
                    type: obj.type,
                    x: obj.x,
                    y: obj.y,
                    width: obj.width,
                    height: obj.height,
                    scaleX: obj.scaleX,
                    scaleY: obj.scaleY,
                    rotation: obj.rotation,
                    opacity: obj.opacity,
                    fillColor: obj.fillColor,
                    strokeColor: obj.strokeColor,
                    strokeWidth: obj.strokeWidth,
                    id: obj.id
                };
            }
        }));
        
        this.history.push(state);
        this.historyIndex++;
        
        // Limit history size
        if (this.history.length > this.maxHistorySize) {
            this.history.shift();
            this.historyIndex--;
        }
    }

    restoreState(stateJson) {
        const state = JSON.parse(stateJson);
        this.objects = [];
        
        // Restore objects
        for (const objData of state) {
            if (objData.type === 'image') {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    const obj = new CanvasObject(img, objData.x, objData.y, 'image');
                    obj.width = objData.width;
                    obj.height = objData.height;
                    obj.scaleX = objData.scaleX || 1;
                    obj.scaleY = objData.scaleY || 1;
                    obj.rotation = objData.rotation || 0;
                    obj.opacity = objData.opacity || 1;
                    obj.cropShape = objData.cropShape || null;
                    obj.outlineWidth = objData.outlineWidth || 0;
                    obj.outlineColor = objData.outlineColor || '#000000';
                    obj.id = objData.id;
                    this.objects.push(obj);
                    this.draw();
                };
                img.src = objData.imageSrc;
            } else if (objData.type === 'text') {
                const obj = new CanvasObject(null, objData.x, objData.y, 'text');
                obj.width = objData.width;
                obj.height = objData.height;
                obj.scaleX = objData.scaleX || 1;
                obj.scaleY = objData.scaleY || 1;
                obj.rotation = objData.rotation || 0;
                obj.opacity = objData.opacity || 1;
                obj.text = objData.text;
                obj.fontFamily = objData.fontFamily;
                obj.fontSize = objData.fontSize;
                obj.textColor = objData.textColor;
                obj.bold = objData.bold || false;
                obj.italic = objData.italic || false;
                obj.id = objData.id;
                this.objects.push(obj);
            }
        }
        
        this.selectedObject = null;
        this.updatePropertiesPanel();
        this.updateLayersList();
        this.draw();
        this.updateDownloadButton();
    }

    undo() {
        if (this.historyIndex <= 0) {
            this.showToast('Nothing to undo', 'error');
            return;
        }
        
        this.historyIndex--;
        const state = this.history[this.historyIndex];
        this.restoreState(state);
        this.showToast('Undone', 'success');
    }

    redo() {
        if (this.historyIndex >= this.history.length - 1) {
            this.showToast('Nothing to redo', 'error');
            return;
        }
        
        this.historyIndex++;
        const state = this.history[this.historyIndex];
        this.restoreState(state);
        this.showToast('Redone', 'success');
    }

    // Copy/Paste system
    copySelectedObject() {
        if (!this.selectedObject) {
            this.showToast('No object selected to copy', 'error');
            return;
        }
        
        // Create a deep copy of the selected object
        const obj = this.selectedObject;
        if (obj.type === 'image') {
            // For images, we need to copy the image data
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = obj.image.width;
            canvas.height = obj.image.height;
            ctx.drawImage(obj.image, 0, 0);
            
            canvas.toBlob((blob) => {
                const reader = new FileReader();
                reader.onload = () => {
                    this.clipboard = {
                        type: 'image',
                        x: obj.x,
                        y: obj.y,
                        width: obj.width,
                        height: obj.height,
                        scaleX: obj.scaleX,
                        scaleY: obj.scaleY,
                        rotation: obj.rotation,
                        opacity: obj.opacity,
                        cropShape: obj.cropShape,
                        outlineWidth: obj.outlineWidth,
                        outlineColor: obj.outlineColor,
                        hasTransparency: obj.hasTransparency,
                        imageData: reader.result
                    };
                    this.showToast('Object copied', 'success');
                };
                reader.readAsDataURL(blob);
            }, 'image/png');
        } else if (obj.type === 'text') {
            this.clipboard = {
                type: 'text',
                x: obj.x,
                y: obj.y,
                width: obj.width,
                height: obj.height,
                scaleX: obj.scaleX,
                scaleY: obj.scaleY,
                rotation: obj.rotation,
                opacity: obj.opacity,
                text: obj.text,
                fontFamily: obj.fontFamily,
                fontSize: obj.fontSize,
                textColor: obj.textColor,
                bold: obj.bold,
                italic: obj.italic,
                underline: obj.underline,
                textAlign: obj.textAlign
            };
            this.showToast('Object copied', 'success');
        } else if (obj.type === 'circle' || obj.type === 'square') {
            this.clipboard = {
                type: obj.type,
                x: obj.x,
                y: obj.y,
                width: obj.width,
                height: obj.height,
                scaleX: obj.scaleX,
                scaleY: obj.scaleY,
                rotation: obj.rotation,
                opacity: obj.opacity,
                fillColor: obj.fillColor,
                strokeColor: obj.strokeColor,
                strokeWidth: obj.strokeWidth
            };
            this.showToast('Object copied', 'success');
        }
    }

    pasteObject() {
        if (!this.clipboard) {
            this.showToast('Nothing to paste', 'error');
            return;
        }
        
        this.saveState();
        
        if (this.clipboard.type === 'image') {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                // Offset pasted object slightly
                const offset = 20;
                const obj = new CanvasObject(img, this.clipboard.x + offset, this.clipboard.y + offset, 'image');
                obj.width = this.clipboard.width;
                obj.height = this.clipboard.height;
                obj.scaleX = this.clipboard.scaleX || 1;
                obj.scaleY = this.clipboard.scaleY || 1;
                obj.rotation = this.clipboard.rotation || 0;
                obj.opacity = this.clipboard.opacity || 1;
                obj.cropShape = this.clipboard.cropShape || null;
                obj.outlineWidth = this.clipboard.outlineWidth || 0;
                obj.outlineColor = this.clipboard.outlineColor || '#000000';
                obj.hasTransparency = this.clipboard.hasTransparency || false;
                
                // Store the first imported image for reset functionality
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                const firstImg = new Image();
                firstImg.src = canvas.toDataURL('image/png');
                obj.firstImportedImage = firstImg;
                obj.firstImportedX = obj.x;
                obj.firstImportedY = obj.y;
                obj.firstImportedWidth = img.width;
                obj.firstImportedHeight = img.height;
                
                // If transparency info wasn't preserved, check for it
                if (!obj.hasTransparency && this.clipboard.imageData && this.clipboard.imageData.startsWith('data:image/png')) {
                    this.checkImageTransparency(img, obj);
                }
                
                this.objects.push(obj);
                this.selectedObjects = [obj];
                this.selectedObject = obj;
                this.updatePropertiesPanel();
                this.updateLayersList();
                this.draw();
                this.updateDownloadButton();
                this.showToast('Object pasted', 'success');
            };
            img.onerror = () => {
                this.showToast('Failed to paste image', 'error');
            };
            img.src = this.clipboard.imageData;
        } else if (this.clipboard.type === 'text') {
            // Offset pasted object slightly
            const offset = 20;
            const obj = new CanvasObject(null, this.clipboard.x + offset, this.clipboard.y + offset, 'text');
            obj.width = this.clipboard.width;
            obj.height = this.clipboard.height;
            obj.scaleX = this.clipboard.scaleX || 1;
            obj.scaleY = this.clipboard.scaleY || 1;
            obj.rotation = this.clipboard.rotation || 0;
            obj.opacity = this.clipboard.opacity || 1;
            obj.text = this.clipboard.text;
            obj.fontFamily = this.clipboard.fontFamily;
            obj.fontSize = this.clipboard.fontSize;
            obj.textColor = this.clipboard.textColor;
            obj.bold = this.clipboard.bold || false;
            obj.italic = this.clipboard.italic || false;
            obj.underline = this.clipboard.underline || false;
            obj.textAlign = this.clipboard.textAlign || 'left';
            
            this.objects.push(obj);
            this.selectedObject = obj;
            this.updatePropertiesPanel();
            this.updateLayersList();
            this.draw();
            this.updateDownloadButton();
            this.showToast('Object pasted', 'success');
        } else if (this.clipboard.type === 'circle' || this.clipboard.type === 'square') {
            // Offset pasted object slightly
            const offset = 20;
            const obj = new CanvasObject(null, this.clipboard.x + offset, this.clipboard.y + offset, this.clipboard.type);
            obj.width = this.clipboard.width;
            obj.height = this.clipboard.height;
            obj.scaleX = this.clipboard.scaleX || 1;
            obj.scaleY = this.clipboard.scaleY || 1;
            obj.rotation = this.clipboard.rotation || 0;
            obj.opacity = this.clipboard.opacity || 1;
            obj.fillColor = this.clipboard.fillColor || '#e96d00';
            obj.strokeColor = this.clipboard.strokeColor || '#000000';
            obj.strokeWidth = this.clipboard.strokeWidth || 0;
            
            this.objects.push(obj);
            this.selectedObject = obj;
            this.updatePropertiesPanel();
            this.updateLayersList();
            this.draw();
            this.updateDownloadButton();
            this.showToast('Object pasted', 'success');
        }
    }

    updateLayersList() {
        const layersList = document.getElementById('layersList');
        if (!layersList) return;
        
        if (this.objects.length === 0) {
            layersList.innerHTML = '<p class="layers-empty">No objects yet</p>';
            return;
        }
        
        // Display layers in reverse order (top to bottom)
        layersList.innerHTML = this.objects.slice().reverse().map((obj, index) => {
            const actualIndex = this.objects.length - 1 - index;
            const isSelected = this.selectedObjects.some(selected => selected.id === obj.id);
            let typeIcon = '🖼️';
            let name = `Object ${actualIndex + 1}`;
            if (obj.type === 'text') {
                typeIcon = '📝';
                name = (obj.text.split('\n')[0] || 'Text').substring(0, 30);
            } else if (obj.type === 'circle') {
                typeIcon = '⭕';
                name = 'Circle';
            } else if (obj.type === 'square') {
                typeIcon = '⬜';
                name = 'Square';
            } else if (obj.type === 'image') {
                name = `Image ${actualIndex + 1}`;
            }
            name = name.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

            return `
                <div class="layer-item ${isSelected ? 'selected' : ''}" data-id="${obj.id}" data-index="${actualIndex}" draggable="true">
                    <span class="layer-item-icon">${typeIcon}</span>
                    <span class="layer-item-name">${name}</span>
                    <span class="layer-item-handle">☰</span>
                </div>
            `;
        }).join('');
        
        // Add event listeners for selection
        layersList.querySelectorAll('.layer-item').forEach(item => {
            item.addEventListener('click', (e) => {
                if (e.target.textContent === '☰') return;
                const id = parseFloat(item.dataset.id);
                this.selectedObject = this.objects.find(obj => obj.id === id);
                this.updatePropertiesPanel();
                this.updateLayersList();
                this.draw();
            });
        });
        
        // Drag and drop functionality
        let draggedElement = null;
        let draggedIndex = null;
        
        layersList.querySelectorAll('.layer-item').forEach(item => {
            item.addEventListener('dragstart', (e) => {
                draggedElement = item;
                draggedIndex = parseInt(item.dataset.index);
                item.style.opacity = '0.5';
                e.dataTransfer.effectAllowed = 'move';
            });
            
            item.addEventListener('dragend', (e) => {
                item.style.opacity = '1';
            });
            
            item.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                
                const afterElement = this.getDragAfterElement(layersList, e.clientY);
                const currentElement = item;
                
                if (afterElement == null) {
                    layersList.appendChild(currentElement);
                } else {
                    layersList.insertBefore(currentElement, afterElement);
                }
            });
            
            item.addEventListener('drop', (e) => {
                e.preventDefault();
                if (!draggedElement || draggedElement === item) return;
                
                const dropIndex = parseInt(item.dataset.index);
                
                // Calculate new positions
                if (draggedIndex < dropIndex) {
                    // Moving down
                    for (let i = draggedIndex; i < dropIndex; i++) {
                        [this.objects[i], this.objects[i + 1]] = [this.objects[i + 1], this.objects[i]];
                    }
                } else {
                    // Moving up
                    for (let i = draggedIndex; i > dropIndex; i--) {
                        [this.objects[i], this.objects[i - 1]] = [this.objects[i - 1], this.objects[i]];
                    }
                }
                
                this.saveState();
                this.updateLayersList();
                this.draw();
            });
        });
    }

    getDragAfterElement(container, y) {
        const draggableElements = [...container.querySelectorAll('.layer-item:not(.dragging)')];
        
        return draggableElements.reduce((closest, child) => {
            const box = child.getBoundingClientRect();
            const offset = y - box.top - box.height / 2;
            
            if (offset < 0 && offset > closest.offset) {
                return { offset: offset, element: child };
            } else {
                return closest;
            }
        }, { offset: Number.NEGATIVE_INFINITY }).element;
    }

    showToast(message, type = 'success') {
        // Simple toast notification
        const toast = document.createElement('div');
        toast.className = `toast ${type}`;
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 1rem 1.5rem;
            background: ${type === 'error' ? '#f8d7da' : '#d4edda'};
            color: ${type === 'error' ? '#721c24' : '#155724'};
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            z-index: 10000;
            animation: slideIn 0.3s ease;
        `;
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 3000);
    }

    handleMouseDown(e) {
        const coords = this.getCanvasCoordinates(e);
        
        // If crop tool is active, handle crop box interaction
        if (this.isCropping) {
            // Ensure crop box exists
            if (!this.cropBox) {
                const obj = this.selectedObject;
                if (obj && obj.type === 'image') {
                    const bounds = (obj.originalX !== null && obj.originalX !== undefined) 
                        ? { x: obj.originalX, y: obj.originalY, width: obj.originalWidth, height: obj.originalHeight }
                        : obj.getBounds();
                    this.cropBox = {
                        x: bounds.x,
                        y: bounds.y,
                        width: bounds.width,
                        height: bounds.height
                    };
                }
            }
            
            if (this.cropBox) {
                const cropHandle = this.getCropResizeHandle(coords.x, coords.y);
                
                if (cropHandle) {
                // Start resizing crop box
                this.isResizingCrop = true;
                this.resizeCorner = cropHandle;
                this.cropStart = { ...this.cropBox };
                this.dragStart = coords;
                this.canvas.style.cursor = this.getResizeCursor(cropHandle);
            } else if (this.isPointInCropBox(coords.x, coords.y)) {
                // Start dragging crop box
                this.isDraggingCrop = true;
                this.dragStart = {
                    x: coords.x - this.cropBox.x,
                    y: coords.y - this.cropBox.y
                };
                this.canvas.style.cursor = 'move';
            } else {
                // Start creating new crop box
                // Check if click is within image bounds (use original bounds in crop mode)
                const obj = this.selectedObject;
                let bounds;
                if (obj.originalImage && obj.originalX !== null) {
                    bounds = {
                        x: obj.originalX,
                        y: obj.originalY,
                        width: obj.originalWidth,
                        height: obj.originalHeight
                    };
                } else {
                    bounds = obj.getBounds();
                }
                
                if (coords.x >= bounds.x && coords.x <= bounds.x + bounds.width &&
                    coords.y >= bounds.y && coords.y <= bounds.y + bounds.height) {
                    this.cropBox = {
                        x: coords.x,
                        y: coords.y,
                        width: 0,
                        height: 0
                    };
                    this.cropStart = { x: coords.x, y: coords.y };
                    this.isDraggingCrop = true;
                } else {
                    // Clicked outside image - apply crop and exit crop mode
                    if (this.cropBox && this.cropBox.width > 0 && this.cropBox.height > 0) {
                        this.applyManualCrop(() => {
                            // Exit crop mode after crop is applied
                            this.exitCropMode();
                            this.currentTool = null;
                            document.querySelectorAll('.tool-btn').forEach(btn => btn.classList.remove('active'));
                        });
                    } else {
                        // No valid crop box, just exit
                        this.exitCropMode();
                        this.currentTool = null;
                        document.querySelectorAll('.tool-btn').forEach(btn => btn.classList.remove('active'));
                    }
                }
                }
                this.draw();
                return;
            }
        }
        
        // Always check for object selection/movement first (regardless of tool)
        // Only allow resizing if single object is selected
        const handle = (this.selectedObjects.length === 1 && this.selectedObject) 
            ? this.selectedObject.getResizeHandle(coords.x, coords.y) 
            : null;
        
        if (handle) {
            // Start resizing (only for single selection)
            this.isResizing = true;
            this.wasResizing = false; // Reset flag
            this.resizeCorner = handle;
            this.initialBounds = { ...this.selectedObject.getBounds() };
            this.dragStart = coords;
            this.canvas.style.cursor = this.getResizeCursor(handle);
        } else {
            // Check if clicking on an object - prioritize object selection/movement
            const clickedObject = this.getObjectAt(coords.x, coords.y);
            const isAlreadySelected = clickedObject && this.selectedObjects.some(obj => obj.id === clickedObject.id);
            
            if (clickedObject) {
                // Multi-select: Shift+click adds to selection, regular click replaces selection
                if (e.shiftKey || e.metaKey || e.ctrlKey) {
                    // Add to selection if not already selected
                    if (!isAlreadySelected) {
                        this.selectedObjects.push(clickedObject);
                        this.selectedObject = clickedObject; // Set as primary
                    } else {
                        // Remove from selection if already selected
                        this.selectedObjects = this.selectedObjects.filter(obj => obj.id !== clickedObject.id);
                        // Update primary selected object
                        this.selectedObject = this.selectedObjects.length > 0 ? this.selectedObjects[this.selectedObjects.length - 1] : null;
                    }
                } else {
                    // Regular click behavior
                    if (isAlreadySelected) {
                        // Clicking on already-selected object: maintain current selection for multi-drag
                        // Update primary selected object to the clicked one
                this.selectedObject = clickedObject;
                    } else {
                        // Clicking on non-selected object: replace selection
                        this.selectedObjects = [clickedObject];
                        this.selectedObject = clickedObject;
                    }
                }
                
                this.updatePropertiesPanel();
                this.updateLayersList();
                
                // Always start dragging if we have selected objects (allows moving all selected objects together)
                if (this.selectedObjects.length > 0) {
                this.isDragging = true;
                    this.wasDragging = false; // Reset flag
                    // Store initial positions of all selected objects
                    this.initialPositions = this.selectedObjects.map(obj => ({
                        id: obj.id,
                        x: obj.x,
                        y: obj.y
                    }));
                    // Calculate drag start relative to the click position
                this.dragStart = {
                        x: coords.x,
                        y: coords.y
                };
                this.canvas.style.cursor = 'move';
                }
                
                // Clear any active tool when selecting an object
                if (this.currentTool) {
                    this.currentTool = null;
                    document.querySelectorAll('.tool-btn').forEach(btn => btn.classList.remove('active'));
                }
            } else {
                // Only add new objects if clicking on empty space AND tool is active
                if (this.currentTool === 'text') {
                    this.addTextObject(coords.x, coords.y);
                    this.currentTool = null;
                    const textTool = document.getElementById('textTool');
                    if (textTool) textTool.classList.remove('active');
                } else if (this.currentTool === 'circle' || this.currentTool === 'square') {
                    const shapeType = this.currentTool;
                    this.addShapeObject(coords.x, coords.y, shapeType);
                    this.currentTool = null;
                    const toolBtn = document.querySelector(`[data-tool="${shapeType}"]`);
                    if (toolBtn) toolBtn.classList.remove('active');
                } else {
                    // No tool active and clicked on empty space
                    // Clear selection unless Shift is held (for box selection in future)
                    if (!e.shiftKey && !e.metaKey && !e.ctrlKey) {
                        this.selectedObjects = [];
                this.selectedObject = null;
                this.updatePropertiesPanel();
                        this.updateLayersList();
                    }
                }
            }
        }
        
        this.draw();
    }

    isPointInCropBox(x, y) {
        if (!this.cropBox) return false;
        return x >= this.cropBox.x && x <= this.cropBox.x + this.cropBox.width &&
               y >= this.cropBox.y && y <= this.cropBox.y + this.cropBox.height;
    }

    handleDoubleClick(e) {
        const coords = this.getCanvasCoordinates(e);
        const clickedObject = this.getObjectAt(coords.x, coords.y);
        
        if (clickedObject && clickedObject.type === 'text') {
            this.selectedObject = clickedObject;
            this.updatePropertiesPanel();
            this.updateLayersList();
            this.startTextEditing(clickedObject);
        }
    }

    startTextEditing(textObj) {
        textObj.isEditing = true;
        
        // Create input element for inline editing
        const input = document.createElement('textarea');
        input.value = textObj.text;
        const container = this.canvas.parentElement;
        const containerRect = container ? container.getBoundingClientRect() : this.canvas.getBoundingClientRect();
        const canvasRect = this.canvas.getBoundingClientRect();
        
        // Calculate position relative to canvas container to prevent shifting
        const canvasX = (textObj.x * this.displayScale);
        const canvasY = (textObj.y * this.displayScale);
        
        // Store initial position for reference
        const initialX = canvasX;
        const initialY = canvasY;
        
        // Set up canvas context to measure text properly
        this.ctx.font = `${textObj.fontSize}px ${textObj.fontFamily}`;
        const lines = textObj.text.split('\n');
        const lineHeight = textObj.fontSize * 1.5;
        const scaledLineHeight = lineHeight * this.displayScale;
        
        input.style.cssText = `
            position: absolute;
            left: ${initialX}px;
            top: ${initialY}px;
            width: ${Math.max(200, textObj.width * this.displayScale + 10)}px;
            min-height: ${Math.max(scaledLineHeight, textObj.height * this.displayScale)}px;
            font: ${textObj.fontSize * this.displayScale}px ${textObj.fontFamily};
            color: ${textObj.textColor};
            border: none;
            border-radius: 0;
            padding: 0;
            margin: 0;
            background: transparent;
            outline: none;
            z-index: 10000;
            resize: none;
            overflow: hidden;
            box-shadow: none;
            line-height: ${scaledLineHeight}px;
            white-space: pre-wrap;
            word-wrap: break-word;
            caret-color: ${textObj.textColor};
            transform: none;
            pointer-events: auto;
        `;
        
        // Hide canvas text while editing
        this.draw();
        
        const finishEditing = () => {
            const oldText = textObj.text;
            textObj.text = input.value;
            textObj.isEditing = false;
            this.ctx.font = `${textObj.fontSize}px ${textObj.fontFamily}`;
            // Handle multi-line text dimensions
            const updatedLines = textObj.text.split('\n');
            const updatedLineHeight = textObj.fontSize * 1.5;
            let maxWidth = 0;
            updatedLines.forEach(line => {
                const metrics = this.ctx.measureText(line);
                maxWidth = Math.max(maxWidth, metrics.width);
            });
            textObj.width = maxWidth;
            textObj.height = updatedLines.length * updatedLineHeight;
            
            // Save state if text actually changed
            if (oldText !== textObj.text) {
                this.saveState();
            }
            
            input.remove();
            this.draw();
        };
        
        input.addEventListener('blur', finishEditing);
        
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                input.blur();
            }
            if (e.key === 'Escape') {
                // Restore original text on escape
                input.value = textObj.text;
                input.blur();
            }
        });
        
        // Auto-resize textarea to fit content without shifting position
        const autoResize = () => {
            // Store current scroll position to prevent jumping
            const scrollTop = input.scrollTop;
            const scrollLeft = input.scrollLeft;
            
            input.style.height = 'auto';
            const newHeight = Math.max(scaledLineHeight, input.scrollHeight);
            input.style.height = newHeight + 'px';
            
            // Restore scroll position to prevent shifting
            input.scrollTop = scrollTop;
            input.scrollLeft = scrollLeft;
            
            // Ensure position stays fixed (prevent any layout shifts)
            input.style.left = `${initialX}px`;
            input.style.top = `${initialY}px`;
        };
        
        input.addEventListener('input', autoResize);
        
        // Append to canvas container to position relative to it
        if (container) {
            container.style.position = 'relative';
            container.appendChild(input);
        } else {
            document.body.appendChild(input);
        }
        input.focus();
        input.select();
        autoResize(); // Initial resize
    }

    handleMouseMove(e) {
        const coords = this.getCanvasCoordinates(e);
        
        // Handle crop box interaction
        if (this.isCropping && this.cropBox) {
            if (this.isResizingCrop) {
                // Resize crop box
                const deltaX = coords.x - this.dragStart.x;
                const deltaY = coords.y - this.dragStart.y;
                
                switch (this.resizeCorner) {
                    case 'nw':
                        this.cropBox.x = this.cropStart.x + deltaX;
                        this.cropBox.y = this.cropStart.y + deltaY;
                        this.cropBox.width = this.cropStart.width - deltaX;
                        this.cropBox.height = this.cropStart.height - deltaY;
                        break;
                    case 'ne':
                        this.cropBox.y = this.cropStart.y + deltaY;
                        this.cropBox.width = this.cropStart.width + deltaX;
                        this.cropBox.height = this.cropStart.height - deltaY;
                        break;
                    case 'sw':
                        this.cropBox.x = this.cropStart.x + deltaX;
                        this.cropBox.width = this.cropStart.width - deltaX;
                        this.cropBox.height = this.cropStart.height + deltaY;
                        break;
                    case 'se':
                        this.cropBox.width = this.cropStart.width + deltaX;
                        this.cropBox.height = this.cropStart.height + deltaY;
                        break;
                    case 'n': // Top edge
                        this.cropBox.y = this.cropStart.y + deltaY;
                        this.cropBox.height = this.cropStart.height - deltaY;
                        break;
                    case 's': // Bottom edge
                        this.cropBox.height = this.cropStart.height + deltaY;
                        break;
                    case 'w': // Left edge
                        this.cropBox.x = this.cropStart.x + deltaX;
                        this.cropBox.width = this.cropStart.width - deltaX;
                        break;
                    case 'e': // Right edge
                        this.cropBox.width = this.cropStart.width + deltaX;
                        break;
                }
                
                // Ensure crop box stays within image bounds (use original bounds in crop mode)
                const obj = this.selectedObject;
                let bounds;
                if (obj.originalImage && obj.originalX !== null) {
                    bounds = {
                        x: obj.originalX,
                        y: obj.originalY,
                        width: obj.originalWidth,
                        height: obj.originalHeight
                    };
                } else {
                    bounds = obj.getBounds();
                }
                this.cropBox.x = Math.max(bounds.x, Math.min(this.cropBox.x, bounds.x + bounds.width));
                this.cropBox.y = Math.max(bounds.y, Math.min(this.cropBox.y, bounds.y + bounds.height));
                this.cropBox.width = Math.max(20, Math.min(this.cropBox.width, bounds.x + bounds.width - this.cropBox.x));
                this.cropBox.height = Math.max(20, Math.min(this.cropBox.height, bounds.y + bounds.height - this.cropBox.y));
                
                this.draw();
            } else if (this.isDraggingCrop) {
                const obj = this.selectedObject;
                // Use original bounds in crop mode
                let bounds;
                if (obj.originalImage && obj.originalX !== null) {
                    bounds = {
                        x: obj.originalX,
                        y: obj.originalY,
                        width: obj.originalWidth,
                        height: obj.originalHeight
                    };
                } else {
                    bounds = obj.getBounds();
                }
                
                if (this.cropBox.width === 0 && this.cropBox.height === 0) {
                    // Creating new crop box - normalize coordinates
                    const deltaX = coords.x - this.cropStart.x;
                    const deltaY = coords.y - this.cropStart.y;
                    
                    // Normalize coordinates (handle dragging in any direction)
                    this.cropBox.x = Math.min(this.cropStart.x, coords.x);
                    this.cropBox.y = Math.min(this.cropStart.y, coords.y);
                    this.cropBox.width = Math.abs(deltaX);
                    this.cropBox.height = Math.abs(deltaY);
                    
                    // Constrain to image bounds
                    this.cropBox.x = Math.max(bounds.x, Math.min(this.cropBox.x, bounds.x + bounds.width - 20));
                    this.cropBox.y = Math.max(bounds.y, Math.min(this.cropBox.y, bounds.y + bounds.height - 20));
                    this.cropBox.width = Math.min(this.cropBox.width, bounds.x + bounds.width - this.cropBox.x);
                    this.cropBox.height = Math.min(this.cropBox.height, bounds.y + bounds.height - this.cropBox.y);
                } else {
                    // Dragging existing crop box
                    this.cropBox.x = coords.x - this.dragStart.x;
                    this.cropBox.y = coords.y - this.dragStart.y;
                    
                    // Keep crop box within image bounds
                    this.cropBox.x = Math.max(bounds.x, Math.min(this.cropBox.x, bounds.x + bounds.width - this.cropBox.width));
                    this.cropBox.y = Math.max(bounds.y, Math.min(this.cropBox.y, bounds.y + bounds.height - this.cropBox.height));
                }
                this.draw();
            } else {
                // Update cursor based on hover
                const cropHandle = this.getCropResizeHandle(coords.x, coords.y);
                if (cropHandle) {
                    this.canvas.style.cursor = this.getResizeCursor(cropHandle);
                } else if (this.isPointInCropBox(coords.x, coords.y)) {
                    this.canvas.style.cursor = 'move';
                } else {
                    this.canvas.style.cursor = 'crosshair';
                }
            }
            return;
        }
        
        if (this.isResizing && this.selectedObject) {
            this.resizeObject(coords);
            this.wasResizing = true; // Mark that resizing occurred
            this.canvas.style.cursor = this.getResizeCursor(this.resizeCorner);
        } else if (this.isDragging && this.selectedObjects.length > 0) {
            // Move all selected objects together
            // Ensure we have initial positions stored
            if (this.initialPositions.length === 0 && this.selectedObjects.length > 0) {
                // Fallback: store initial positions now if not already stored
                this.initialPositions = this.selectedObjects.map(obj => ({
                    id: obj.id,
                    x: obj.x,
                    y: obj.y
                }));
            }
            
            if (this.initialPositions.length > 0) {
                // Calculate total offset from initial click position
                const deltaX = coords.x - this.dragStart.x;
                const deltaY = coords.y - this.dragStart.y;
                
                // Move all selected objects by the same offset from their initial positions
                this.selectedObjects.forEach(obj => {
                    const initialPos = this.initialPositions.find(pos => pos.id === obj.id);
                    if (initialPos) {
                        obj.x = initialPos.x + deltaX;
                        obj.y = initialPos.y + deltaY;
                    }
                });
                
                this.wasDragging = true; // Mark that dragging occurred
            this.canvas.style.cursor = 'move';
            this.draw();
            }
        } else {
            // Update cursor based on hover
            const handle = this.selectedObject?.getResizeHandle(coords.x, coords.y);
            if (handle) {
                this.canvas.style.cursor = this.getResizeCursor(handle);
            } else if (this.getObjectAt(coords.x, coords.y)) {
                this.canvas.style.cursor = 'move';
            } else {
                this.canvas.style.cursor = 'default';
            }
        }
    }

    handleMouseUp(e) {
        if (this.isDraggingCrop || this.isResizingCrop) {
            const wasCreating = this.isDraggingCrop && this.cropBox && 
                (this.cropBox.width === 0 || this.cropBox.height === 0 || 
                 (this.cropStart && this.cropStart.x === this.cropBox.x && this.cropStart.y === this.cropBox.y));
            
            this.isDraggingCrop = false;
            this.isResizingCrop = false;
            this.resizeCorner = null;
            this.canvas.style.cursor = 'default';
            
            // Apply crop automatically when user finishes dragging/resizing (if valid)
            if (this.cropBox && this.cropBox.width > 20 && this.cropBox.height > 20) {
                // Apply crop automatically when user finishes dragging/resizing
                this.applyManualCrop();
            } else if (this.cropBox && (this.cropBox.width <= 20 || this.cropBox.height <= 20)) {
                // Crop box too small, reset to full image bounds (use original bounds in crop mode)
                const obj = this.selectedObject;
                if (obj && obj.type === 'image') {
                    let bounds;
                    if (obj.originalImage && obj.originalX !== null) {
                        bounds = {
                            x: obj.originalX,
                            y: obj.originalY,
                            width: obj.originalWidth,
                            height: obj.originalHeight
                        };
                    } else {
                        bounds = obj.getBounds();
                    }
                    this.cropBox = {
                        x: bounds.x,
                        y: bounds.y,
                        width: bounds.width,
                        height: bounds.height
                    };
                    this.draw();
                }
            }
        }
        
        if (this.isDragging || this.isResizing) {
            // Save state if object was actually moved or resized
            if ((this.isDragging && this.wasDragging) || (this.isResizing && this.wasResizing)) {
                this.saveState();
                
                // If image was resized, update the original image reference to the resized version
                if (this.isResizing && this.wasResizing && this.selectedObject && this.selectedObject.type === 'image') {
                    this.updateOriginalImageReference(this.selectedObject);
                }
            }
            this.isDragging = false;
            this.isResizing = false;
            this.wasDragging = false;
            this.wasResizing = false;
            this.initialPositions = [];
            this.resizeCorner = null;
            this.canvas.style.cursor = 'default';
        }
    }

    resizeObject(coords) {
        if (!this.selectedObject || !this.initialBounds) return;
        
        const dx = coords.x - this.dragStart.x;
        const dy = coords.y - this.dragStart.y;
        const corner = this.resizeCorner;
        
        let newWidth = this.initialBounds.width;
        let newHeight = this.initialBounds.height;
        let newX = this.initialBounds.x;
        let newY = this.initialBounds.y;
        
        // Calculate new dimensions based on corner
        if (corner.includes('e')) {
            newWidth = this.initialBounds.width + dx;
        }
        if (corner.includes('w')) {
            newWidth = this.initialBounds.width - dx;
            newX = this.initialBounds.x + dx;
        }
        if (corner.includes('s')) {
            newHeight = this.initialBounds.height + dy;
        }
        if (corner.includes('n')) {
            newHeight = this.initialBounds.height - dy;
            newY = this.initialBounds.y + dy;
        }
        
        // Maintain aspect ratio
        const aspectRatio = this.selectedObject.width / this.selectedObject.height;
        if (Math.abs(dx) > Math.abs(dy)) {
            newHeight = newWidth / aspectRatio;
            if (corner.includes('n')) {
                newY = this.initialBounds.y + this.initialBounds.height - newHeight;
            }
        } else {
            newWidth = newHeight * aspectRatio;
            if (corner.includes('w')) {
                newX = this.initialBounds.x + this.initialBounds.width - newWidth;
            }
        }
        
        // Apply minimum size
        const minSize = 50;
        if (newWidth < minSize) {
            newWidth = minSize;
            newHeight = minSize / aspectRatio;
        }
        if (newHeight < minSize) {
            newHeight = minSize;
            newWidth = minSize * aspectRatio;
        }
        
        // Update object
        this.selectedObject.scaleX = newWidth / this.selectedObject.width;
        this.selectedObject.scaleY = newHeight / this.selectedObject.height;
        this.selectedObject.x = newX;
        this.selectedObject.y = newY;
        
        this.draw();
    }

    getResizeCursor(corner) {
        const cursors = {
            'nw': 'nw-resize',
            'ne': 'ne-resize',
            'sw': 'sw-resize',
            'se': 'se-resize',
            'n': 'n-resize',
            's': 's-resize',
            'w': 'w-resize',
            'e': 'e-resize'
        };
        return cursors[corner] || 'default';
    }

    getObjectAt(x, y) {
        // Check from top to bottom (last object is on top)
        for (let i = this.objects.length - 1; i >= 0; i--) {
            const obj = this.objects[i];
            if (obj.type === 'text') {
                // Text object hit detection
                if (x >= obj.x && x <= obj.x + obj.width &&
                    y >= obj.y && y <= obj.y + obj.height) {
                    return obj;
                }
            } else if (obj.type === 'circle') {
                // Circle hit detection
                const bounds = obj.getBounds();
                const radius = Math.min(bounds.width, bounds.height) / 2;
                const centerX = obj.x + bounds.width / 2;
                const centerY = obj.y + bounds.height / 2;
                const dist = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
                if (dist <= radius) {
                    return obj;
                }
            } else if (obj.containsPoint(x, y)) {
                return obj;
            }
        }
        return null;
    }

    draw() {
        if (!this.ctx || !this.canvas) return;
        
        // Clear canvas
        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        
        // If no objects, just show empty canvas (allow deletion of last object)
        if (this.objects.length === 0) {
            return;
        }
        
        // Draw all objects
        this.objects.forEach(obj => {
            this.drawObject(obj);
        });
        
        // Draw selection and handles for all selected objects (but not if text is being edited)
        this.selectedObjects.forEach(obj => {
            if (!obj.isEditing) {
                this.drawSelection(obj);
            }
        });
        
        // Draw crop box if in crop mode
        if (this.isCropping && this.cropBox) {
            this.drawCropBox();
        }

        // Draw a per-image loading indicator for async operations (e.g. background removal)
        if (this.processingObject) {
            this.drawProcessingIndicator(this.processingObject);
        }
    }

    startProcessingIndicator(obj) {
        this.processingObject = obj;
        const animate = () => {
            if (this.processingObject !== obj) return;
            this.draw();
            this._processingAnimFrame = requestAnimationFrame(animate);
        };
        this._processingAnimFrame = requestAnimationFrame(animate);
    }

    stopProcessingIndicator() {
        this.processingObject = null;
        if (this._processingAnimFrame) {
            cancelAnimationFrame(this._processingAnimFrame);
            this._processingAnimFrame = null;
        }
        this.draw();
    }

    drawProcessingIndicator(obj) {
        const bounds = obj.getBounds();
        const ctx = this.ctx;

        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
        ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);

        const centerX = bounds.x + bounds.width / 2;
        const centerY = bounds.y + bounds.height / 2;
        const radius = Math.max(20, Math.min(60, Math.min(bounds.width, bounds.height) * 0.15));
        const lineWidth = Math.max(4, radius * 0.18);
        const angle = (Date.now() / 400) % (Math.PI * 2);

        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.stroke();

        ctx.strokeStyle = '#e96d00';
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, angle, angle + Math.PI * 1.2);
        ctx.stroke();
        ctx.restore();
    }

    drawCropBox() {
        if (!this.cropBox) return;
        
        const ctx = this.ctx;
        const box = this.cropBox;
        
        // Draw overlay (darken outside crop area)
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Clear the crop area
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillRect(box.x, box.y, box.width, box.height);
        ctx.globalCompositeOperation = 'source-over';
        ctx.restore();
        
        // Draw crop box border
        ctx.strokeStyle = '#e96d00';
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        ctx.strokeRect(box.x, box.y, box.width, box.height);
        
        // Draw corner handles (larger for easier clicking - 180% increase)
        const handleSize = 29; // 180% of 16
        ctx.fillStyle = '#e96d00';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        const corners = [
            { x: box.x, y: box.y }, // nw
            { x: box.x + box.width, y: box.y }, // ne
            { x: box.x, y: box.y + box.height }, // sw
            { x: box.x + box.width, y: box.y + box.height } // se
        ];
        
        corners.forEach(corner => {
            ctx.fillRect(corner.x - handleSize/2, corner.y - handleSize/2, handleSize, handleSize);
            ctx.strokeRect(corner.x - handleSize/2, corner.y - handleSize/2, handleSize, handleSize);
        });
        
        // Draw side handles (larger for easier clicking - 180% increase)
        const sideHandleWidth = 22; // 180% of 12
        const sideHandleLength = 54; // 180% of 30
        ctx.fillStyle = '#e96d00';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        
        // Top edge
        ctx.fillRect(box.x + box.width/2 - sideHandleLength/2, box.y - sideHandleWidth/2, sideHandleLength, sideHandleWidth);
        ctx.strokeRect(box.x + box.width/2 - sideHandleLength/2, box.y - sideHandleWidth/2, sideHandleLength, sideHandleWidth);
        // Bottom edge
        ctx.fillRect(box.x + box.width/2 - sideHandleLength/2, box.y + box.height - sideHandleWidth/2, sideHandleLength, sideHandleWidth);
        ctx.strokeRect(box.x + box.width/2 - sideHandleLength/2, box.y + box.height - sideHandleWidth/2, sideHandleLength, sideHandleWidth);
        // Left edge
        ctx.fillRect(box.x - sideHandleWidth/2, box.y + box.height/2 - sideHandleLength/2, sideHandleWidth, sideHandleLength);
        ctx.strokeRect(box.x - sideHandleWidth/2, box.y + box.height/2 - sideHandleLength/2, sideHandleWidth, sideHandleLength);
        // Right edge
        ctx.fillRect(box.x + box.width - sideHandleWidth/2, box.y + box.height/2 - sideHandleLength/2, sideHandleWidth, sideHandleLength);
        ctx.strokeRect(box.x + box.width - sideHandleWidth/2, box.y + box.height/2 - sideHandleLength/2, sideHandleWidth, sideHandleLength);
    }

    drawObject(obj) {
        this.ctx.save();
        this.ctx.globalAlpha = obj.opacity;
        
        if (obj.type === 'text') {
            this.drawTextObject(obj);
        } else if (obj.type === 'image') {
            this.drawImageObject(obj);
        } else if (obj.type === 'circle' || obj.type === 'square') {
            this.drawShapeObject(obj);
        }
        
        this.ctx.restore();
    }

    drawImageObject(obj) {
        // In crop mode, show both original image (with reduced opacity) and current cropped image
        if (this.isCropping && this.selectedObject === obj && obj.originalImage) {
            // Draw original image with reduced opacity as reference at its original resized size
            const originalBounds = (obj.originalX !== null && obj.originalX !== undefined)
                ? { x: obj.originalX, y: obj.originalY, width: obj.originalWidth, height: obj.originalHeight }
                : obj.getBounds();
            
            this.ctx.save();
            this.ctx.globalAlpha = 0.3; // Reduced opacity for reference
            // Draw original image at its original resized position and size
            this.ctx.drawImage(
                obj.originalImage,
                originalBounds.x,
                originalBounds.y,
                originalBounds.width,
                originalBounds.height
            );
            this.ctx.restore();
            
            // Draw current cropped image on top at its current size
            const bounds = obj.getBounds();
            this.ctx.save();
            this.ctx.globalAlpha = obj.opacity;
            this.ctx.drawImage(
                obj.image,
                bounds.x,
                bounds.y,
                bounds.width,
                bounds.height
            );
            
            // Draw outline if specified
            if (obj.outlineWidth > 0) {
                this.ctx.strokeStyle = obj.outlineColor;
                this.ctx.lineWidth = obj.outlineWidth;
                this.ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
            }
            this.ctx.restore();
            return; // Early return, we've drawn everything
        }
        
        // Normal mode - use current image and current bounds
        const bounds = obj.getBounds();
        const imageToDraw = obj.image;
        
        // Create clipping path for shape cropping (only if not in crop mode)
        if (!this.isCropping && obj.cropShape === 'square') {
            const size = Math.min(bounds.width, bounds.height);
            this.ctx.beginPath();
            this.ctx.rect(obj.x, obj.y, size, size);
            this.ctx.clip();
        } else if (!this.isCropping && obj.cropShape === 'circle') {
            const radius = Math.min(bounds.width, bounds.height) / 2;
            const centerX = obj.x + bounds.width / 2;
            const centerY = obj.y + bounds.height / 2;
            this.ctx.beginPath();
            this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            this.ctx.clip();
        }
        
        // Draw image
        this.ctx.drawImage(
            imageToDraw,
            bounds.x,
            bounds.y,
            bounds.width,
            bounds.height
        );
        
        // Reset clipping
        this.ctx.restore();
        this.ctx.save();
        this.ctx.globalAlpha = obj.opacity;
        
        // Draw outline if specified
        if (obj.outlineWidth > 0) {
            this.ctx.strokeStyle = obj.outlineColor;
            this.ctx.lineWidth = obj.outlineWidth;
            
            if (obj.cropShape === 'circle') {
                const radius = Math.min(bounds.width, bounds.height) / 2;
                const centerX = obj.x + bounds.width / 2;
                const centerY = obj.y + bounds.height / 2;
                this.ctx.beginPath();
                this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                this.ctx.stroke();
            } else if (obj.cropShape === 'square') {
                const size = Math.min(bounds.width, bounds.height);
                this.ctx.strokeRect(obj.x, obj.y, size, size);
            } else if (obj.hasTransparency) {
                this.drawImageOutlineHalo(this.ctx, obj, bounds);
            } else {
                this.ctx.strokeRect(obj.x, obj.y, bounds.width, bounds.height);
            }
        }
    }

    getOutlineSilhouette(obj) {
        const img = obj.image;
        const color = obj.outlineColor;
        if (obj._outlineSilhouetteCanvas && obj._outlineSilhouetteSrc === img && obj._outlineSilhouetteColor === color) {
            return obj._outlineSilhouetteCanvas;
        }

        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        const silhouette = document.createElement('canvas');
        silhouette.width = width;
        silhouette.height = height;
        const silhouetteCtx = silhouette.getContext('2d');
        silhouetteCtx.drawImage(img, 0, 0, width, height);
        silhouetteCtx.globalCompositeOperation = 'source-in';
        silhouetteCtx.fillStyle = color;
        silhouetteCtx.fillRect(0, 0, width, height);

        obj._outlineSilhouetteCanvas = silhouette;
        obj._outlineSilhouetteSrc = img;
        obj._outlineSilhouetteColor = color;
        return silhouette;
    }

    buildOutlineRing(obj, bounds) {
        // Builds a ring-only halo the exact width of outlineWidth: stamp the silhouette
        // in a circle around the origin to grow it outward, then punch the original
        // image's own alpha shape back out (including soft/anti-aliased edges) so the
        // ring never overlaps the image itself - just its outer (and any inner) edges.
        const silhouette = this.getOutlineSilhouette(obj);
        // More steps as the outline gets wider, so the ring stays smooth instead of scalloped
        const steps = Math.min(72, Math.max(24, Math.ceil(obj.outlineWidth * 1.5)));
        const pad = Math.ceil(obj.outlineWidth) + 2;

        const ringCanvas = document.createElement('canvas');
        ringCanvas.width = Math.ceil(bounds.width) + pad * 2;
        ringCanvas.height = Math.ceil(bounds.height) + pad * 2;
        const ringCtx = ringCanvas.getContext('2d');

        for (let i = 0; i < steps; i++) {
            const angle = (i / steps) * Math.PI * 2;
            const dx = Math.cos(angle) * obj.outlineWidth;
            const dy = Math.sin(angle) * obj.outlineWidth;
            ringCtx.drawImage(silhouette, pad + dx, pad + dy, bounds.width, bounds.height);
        }

        ringCtx.globalCompositeOperation = 'destination-out';
        ringCtx.drawImage(obj.image, pad, pad, bounds.width, bounds.height);
        ringCtx.globalCompositeOperation = 'source-over';

        return { canvas: ringCanvas, pad };
    }

    drawImageOutlineHalo(ctx, obj, bounds) {
        // Traces the image's actual (transparent-background) silhouette instead of its
        // rectangular bounds - the ring is pre-masked to exclude the image's own pixels,
        // so it only ever appears around the image, never touching its inside.
        const { canvas: ring, pad } = this.buildOutlineRing(obj, bounds);
        ctx.drawImage(ring, bounds.x - pad, bounds.y - pad);
        ctx.drawImage(obj.image, bounds.x, bounds.y, bounds.width, bounds.height);
    }

    drawTextObject(obj) {
        // Don't draw text if it's being edited (textarea will show it instead)
        if (obj.isEditing) {
            return;
        }
        
        // Build font string with bold and italic
        let fontStyle = '';
        if (obj.bold) fontStyle += 'bold ';
        if (obj.italic) fontStyle += 'italic ';
        this.ctx.font = `${fontStyle}${obj.fontSize}px ${obj.fontFamily}`;
        this.ctx.fillStyle = obj.textColor;
        this.ctx.textBaseline = 'top';
        
        // Handle multi-line text
        const lines = obj.text.split('\n');
        const lineHeight = obj.fontSize * 1.5;
        
        // Calculate max width first for alignment
        let maxWidth = 0;
        lines.forEach(line => {
            const metrics = this.ctx.measureText(line);
            maxWidth = Math.max(maxWidth, metrics.width);
        });
        
        // Set text alignment
        const textAlign = obj.textAlign || 'left';
        this.ctx.textAlign = textAlign;
        
        lines.forEach((line, index) => {
            let x = obj.x;
            // Calculate x position based on alignment
            if (textAlign === 'center') {
                x = obj.x + maxWidth / 2;
            } else if (textAlign === 'right') {
                x = obj.x + maxWidth;
            }
            
            const y = obj.y + (index * lineHeight);
            this.ctx.fillText(line, x, y);
            
            // Draw underline if enabled
            if (obj.underline) {
                const metrics = this.ctx.measureText(line);
                const underlineY = y + obj.fontSize + 2;
                this.ctx.strokeStyle = obj.textColor;
                this.ctx.lineWidth = 1;
                this.ctx.beginPath();
                if (textAlign === 'center') {
                    this.ctx.moveTo(x - metrics.width / 2, underlineY);
                    this.ctx.lineTo(x + metrics.width / 2, underlineY);
                } else if (textAlign === 'right') {
                    this.ctx.moveTo(x - metrics.width, underlineY);
                    this.ctx.lineTo(x, underlineY);
                } else {
                    this.ctx.moveTo(x, underlineY);
                    this.ctx.lineTo(x + metrics.width, underlineY);
                }
                this.ctx.stroke();
            }
        });
        
        // Update dimensions
        obj.width = maxWidth;
        obj.height = lines.length * lineHeight;
    }

    drawShapeObject(obj) {
        const bounds = obj.getBounds();
        
        this.ctx.fillStyle = obj.fillColor || '#e96d00';
        this.ctx.strokeStyle = obj.strokeColor || '#000000';
        this.ctx.lineWidth = obj.strokeWidth || 0;
        
        if (obj.type === 'circle') {
            const radius = Math.min(bounds.width, bounds.height) / 2;
            const centerX = obj.x + bounds.width / 2;
            const centerY = obj.y + bounds.height / 2;
            
            this.ctx.beginPath();
            this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            this.ctx.fill();
            
            if (obj.strokeWidth > 0) {
                this.ctx.stroke();
            }
        } else if (obj.type === 'square') {
            this.ctx.fillRect(obj.x, obj.y, bounds.width, bounds.height);
            
            if (obj.strokeWidth > 0) {
                this.ctx.strokeRect(obj.x, obj.y, bounds.width, bounds.height);
            }
        }
    }

    drawSelection(obj) {
        const bounds = obj.getBounds();
        const handleSize = 30; // Increased for easier clicking (3.75x original size)
        
        // For images with transparency, skip selection border (outline is drawn in drawImageObject)
        if (obj.type === 'image' && obj.hasTransparency && obj.outlineWidth > 0) {
            // Don't draw selection border - outline is already drawn
        } else {
            // Draw selection border for regular images and other objects
            this.ctx.strokeStyle = '#e96d00';
            this.ctx.lineWidth = 2;
            this.ctx.setLineDash([5, 5]);
            
            if (obj.type === 'circle') {
                const radius = Math.min(bounds.width, bounds.height) / 2;
                const centerX = obj.x + bounds.width / 2;
                const centerY = obj.y + bounds.height / 2;
                this.ctx.beginPath();
                this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                this.ctx.stroke();
            } else if (obj.cropShape === 'circle') {
                const radius = Math.min(bounds.width, bounds.height) / 2;
                const centerX = obj.x + bounds.width / 2;
                const centerY = obj.y + bounds.height / 2;
                this.ctx.beginPath();
                this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                this.ctx.stroke();
            } else if (obj.cropShape === 'square') {
                const size = Math.min(bounds.width, bounds.height);
                this.ctx.strokeRect(obj.x, obj.y, size, size);
            } else {
                this.ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
            }
            
            this.ctx.setLineDash([]);
        }
        
        // Draw resize handles (corners) - larger for easier clicking
        const handles = [
            { x: bounds.x, y: bounds.y },
            { x: bounds.x + bounds.width, y: bounds.y },
            { x: bounds.x, y: bounds.y + bounds.height },
            { x: bounds.x + bounds.width, y: bounds.y + bounds.height }
        ];
        
        this.ctx.fillStyle = '#ffffff';
        this.ctx.strokeStyle = '#e96d00';
        this.ctx.lineWidth = 2;
        
        handles.forEach(handle => {
            this.ctx.beginPath();
            this.ctx.arc(handle.x, handle.y, handleSize, 0, Math.PI * 2);
            this.ctx.fill();
            this.ctx.stroke();
        });
    }

    // fitToScreen removed - canvas now sizes directly to fit container

    updateDownloadButton() {
        const downloadBtn = document.getElementById('downloadBtn');
        if (downloadBtn) {
            downloadBtn.disabled = this.objects.length === 0;
        }
    }

    download() {
        const exportCanvas = this.buildExportCanvas();
        if (!exportCanvas) return;

        const link = document.createElement('a');
        link.download = `canvas-${Date.now()}.png`;
        link.href = exportCanvas.toDataURL('image/png');
        link.click();
    }

    buildExportCanvas() {
        if (!this.canvas || this.objects.length === 0) return null;

        // Create a new canvas for export at full resolution
        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = this.canvasWidth;
        exportCanvas.height = this.canvasHeight;
        const exportCtx = exportCanvas.getContext('2d');
        
        // Fill with white background
        exportCtx.fillStyle = '#ffffff';
        exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
        
        // Draw all objects in layer order
        this.objects.forEach(obj => {
            exportCtx.save();
            exportCtx.globalAlpha = obj.opacity;
            
            if (obj.type === 'text') {
                exportCtx.font = `${obj.fontSize}px ${obj.fontFamily}`;
                exportCtx.fillStyle = obj.textColor;
                exportCtx.textAlign = 'left';
                exportCtx.textBaseline = 'top';
                // Handle multi-line text
                const lines = obj.text.split('\n');
                const lineHeight = obj.fontSize * 1.5;
                lines.forEach((line, index) => {
                    exportCtx.fillText(line, obj.x, obj.y + (index * lineHeight));
                });
            } else if (obj.type === 'circle' || obj.type === 'square') {
                const bounds = obj.getBounds();
                exportCtx.fillStyle = obj.fillColor || '#e96d00';
                exportCtx.strokeStyle = obj.strokeColor || '#000000';
                exportCtx.lineWidth = obj.strokeWidth || 0;
                
                if (obj.type === 'circle') {
                    const radius = Math.min(bounds.width, bounds.height) / 2;
                    const centerX = obj.x + bounds.width / 2;
                    const centerY = obj.y + bounds.height / 2;
                    exportCtx.beginPath();
                    exportCtx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                    exportCtx.fill();
                    if (obj.strokeWidth > 0) {
                        exportCtx.stroke();
                    }
                } else if (obj.type === 'square') {
                    exportCtx.fillRect(obj.x, obj.y, bounds.width, bounds.height);
                    if (obj.strokeWidth > 0) {
                        exportCtx.strokeRect(obj.x, obj.y, bounds.width, bounds.height);
                    }
                }
            } else if (obj.type === 'image') {
                const bounds = obj.getBounds();
                
                // Apply shape cropping
                if (obj.cropShape === 'square') {
                    const size = Math.min(bounds.width, bounds.height);
                    exportCtx.beginPath();
                    exportCtx.rect(obj.x, obj.y, size, size);
                    exportCtx.clip();
                } else if (obj.cropShape === 'circle') {
                    const radius = Math.min(bounds.width, bounds.height) / 2;
                    const centerX = obj.x + bounds.width / 2;
                    const centerY = obj.y + bounds.height / 2;
                    exportCtx.beginPath();
                    exportCtx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                    exportCtx.clip();
                }
                
                exportCtx.drawImage(obj.image, obj.x, obj.y, bounds.width, bounds.height);
                exportCtx.restore();
                exportCtx.save();
                exportCtx.globalAlpha = obj.opacity;
                
                // Draw outline
                if (obj.outlineWidth > 0) {
                    exportCtx.strokeStyle = obj.outlineColor;
                    exportCtx.lineWidth = obj.outlineWidth;
                    if (obj.cropShape === 'circle') {
                        const radius = Math.min(bounds.width, bounds.height) / 2;
                        const centerX = obj.x + bounds.width / 2;
                        const centerY = obj.y + bounds.height / 2;
                        exportCtx.beginPath();
                        exportCtx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                        exportCtx.stroke();
                    } else if (obj.cropShape === 'square') {
                        const size = Math.min(bounds.width, bounds.height);
                        exportCtx.strokeRect(obj.x, obj.y, size, size);
                    } else if (obj.hasTransparency) {
                        this.drawImageOutlineHalo(exportCtx, obj, bounds);
                    } else {
                        exportCtx.strokeRect(obj.x, obj.y, bounds.width, bounds.height);
                    }
                }
            }
            
            exportCtx.restore();
        });

        return exportCanvas;
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.canvasEditor = new CanvasEditor();
    });
} else {
    window.canvasEditor = new CanvasEditor();
}
